import { Link, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Area,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts'
import {
  ChevronDown,
  CalendarClock,
  CalendarRange,
  Building2,
  Briefcase,
  Users,
  ClipboardList,
  DollarSign,
  ArrowRight,
  AlertCircle,
  AlertTriangle,
  Wallet,
  ReceiptText,
  CircleDollarSign,
  CheckCircle2,
  Ban,
  CircleDot,
  FileClock,
  FolderTree,
  GitBranch,
  Handshake,
  KeyRound,
  Settings,
  ShieldCheck,
  Landmark,
  Clock,
  CalendarX,
  UserX,
} from 'lucide-react'

import { orgTreeApi, type OrgTreeServiceInstance } from '@/services/org-tree'
import { dashboardApi, type MonthlyTrendPoint } from '@/services/dashboard'
import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'
import { orgRolesApi } from '@/services/org-roles'
import { orgUsersApi } from '@/services/org-users'
import { tenantsApi } from '@/services/tenants'
import { workersApi } from '@/services/workers'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { evaluateRouteAccess } from '@/lib/route-access'
import { getRegistrationStatusBadgeClass, getRegistrationStatusLabel } from '@/lib/registration-status'
import { getServiceInstanceIdFromSearch, ORG_BRANCH_WORKSPACE_PATH, ORG_ORGANIZATION_PAGE_PATH, ORG_SERVICE_INSTANCE_LIST_BASE, resolveActiveServiceInstanceId, resolveScopedPostForServiceInstance, resolveWorkspaceActiveServiceInstanceId, resolveWorkspacePostContext, withServiceInstanceId } from '@/lib/service-instance'
import { useNodeLinkedServiceInstances } from '@/hooks/use-node-linked-service-instances'
import { getLocationReturnPath, withReturnTo } from '@/lib/return-navigation'
import { getOrganizationTreeSetupLabel, isOrganizationServiceVariant, isTenantOrganizationServiceVariant } from '@/lib/service-variant-types'
import { isBranchOperatorWorkspaceContextFromPost } from '@/lib/org-hierarchy-from-instance'
import { formatCurrency, formatDate, getCurrencySymbol } from '@/lib/utils'
import { formatDateOnly, parseServerDate, toTenantDateKey, toTenantDayKey, useTenantTimeZone } from '@/lib/tenant-date-time'
import { BRANCH_CATALOG_LEVEL_VARIANT_UNIQUE_ID } from '@/services/org-level-variants'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { UnassignedAssignmentsAttention } from '@/components/jobs/unassigned-assignments-attention'
import { WorkDayEntriesAttention } from '@/components/jobs/work-day-entries-attention'
import { WorkDayAttendanceAlerts, GroupedAttendanceAlertList } from '@/components/jobs/work-day-attendance-alerts'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { JobTitleLink, formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { useAuthStore } from '@/stores/auth-store'
import { resolveAssignmentStatus } from '@/lib/assignment-status'
import type { Assignment, DashboardAttentionWorkDayEntryItem, DashboardClashedScheduleItem, DashboardExpenseWatchItem, DashboardPendingInvoiceItem, DashboardPeriodType, DashboardUnassignedAssignmentItem, DashboardUpcomingWorkdayScheduleItem, DashboardWorkerPaymentWatchItem, WorkDayEntry } from '@/types'

const PERIOD_OPTIONS: Array<{ value: DashboardPeriodType; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom' },
]

const toInputDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset()
  const localDate = new Date(date.getTime() - offset * 60 * 1000)
  return localDate.toISOString().slice(0, 10)
}

const getLastSevenDaysRange = () => {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - 6)
  return {
    start: toInputDate(start),
    end: toInputDate(end),
  }
}

const SERVICE_STATUS_ORDER = ['Approved', 'Pending', 'Rejected', 'Suspended', 'Terminated', 'Unknown'] as const
const GRAPH_TONES = [
  'bg-gradient-to-r from-amber-400 to-orange-500',
  'bg-gradient-to-r from-emerald-400 to-teal-500',
  'bg-gradient-to-r from-sky-400 to-blue-500',
  'bg-gradient-to-r from-fuchsia-400 to-pink-500',
  'bg-gradient-to-r from-orange-400 to-rose-500',
  'bg-gradient-to-r from-indigo-400 to-violet-500',
] as const

const CHILD_STAT_CARD_TONES = [
  {
    panelTone: 'border-orange-200/70 bg-gradient-to-br from-orange-50 to-amber-50',
    iconTone: 'bg-gradient-to-br from-orange-400 to-amber-500 text-white shadow-sm shadow-orange-300/50',
    labelTone: 'text-orange-700',
    captionTone: 'text-orange-800/80',
  },
  {
    panelTone: 'border-amber-200/70 bg-gradient-to-br from-amber-50 to-yellow-50',
    iconTone: 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white shadow-sm shadow-amber-300/50',
    labelTone: 'text-amber-700',
    captionTone: 'text-amber-800/80',
  },
  {
    panelTone: 'border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-teal-50',
    iconTone: 'bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-sm shadow-emerald-300/50',
    labelTone: 'text-emerald-700',
    captionTone: 'text-emerald-800/80',
  },
  {
    panelTone: 'border-violet-200/70 bg-gradient-to-br from-violet-50 to-indigo-50',
    iconTone: 'bg-gradient-to-br from-violet-500 to-indigo-500 text-white shadow-sm shadow-violet-300/50',
    labelTone: 'text-violet-700',
    captionTone: 'text-violet-800/80',
  },
  {
    panelTone: 'border-sky-200/70 bg-gradient-to-br from-sky-50 to-cyan-50',
    iconTone: 'bg-gradient-to-br from-sky-400 to-cyan-500 text-white shadow-sm shadow-sky-300/50',
    labelTone: 'text-sky-700',
    captionTone: 'text-sky-800/80',
  },
] as const

function DashboardStatSkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div className="grid gap-1.5 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} className="rounded-2xl border-border/50 bg-muted/15">
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-16" />
                <Skeleton className="h-3 w-full max-w-32" />
              </div>
              <Skeleton className="h-8 w-8 rounded-lg" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function DashboardListSkeleton({ count = 4, height = 'h-12' }: { count?: number; height?: string }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className={`${height} w-full rounded-lg`} />
      ))}
    </div>
  )
}

function useLazySection(rootMargin = '280px') {
  const ref = useRef<HTMLDivElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isVisible) {
      return
    }

    const node = ref.current
    if (!node) {
      return
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [isVisible, rootMargin])

  return { ref, isVisible }
}

function normalizeServiceStatus(value: unknown) {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) {
    return 'Unknown'
  }

  if (normalized === '1' || normalized === 'approved' || normalized === 'active') {
    return 'Approved'
  }

  if (normalized === '0' || normalized === 'pending' || normalized === 'inactive') {
    return 'Pending'
  }

  if (normalized === '2' || normalized === 'rejected') {
    return 'Rejected'
  }

  if (normalized === '3' || normalized === 'suspended') {
    return 'Suspended'
  }

  if (normalized === '4' || normalized === 'terminated' || normalized === 'resigned' || normalized === 'deleted') {
    return 'Terminated'
  }

  return String(value)
}

function isApprovedServiceInstance(row: { status?: unknown; isActive?: boolean }) {
  if (row.isActive === false) {
    return false
  }

  const normalizedStatus = normalizeServiceStatus(row.status)
  return normalizedStatus === 'Approved'
}

type DashboardBranchRow = {
  id: string | number
  name: string
  status?: string | number | null
  isActive?: boolean
  levelVariantName?: string | null
  levelVariantTierCode?: string | null
  serviceVariantName?: string | null
  branchOwnershipLabel?: string | null
  isVendorBranch?: boolean
}

function mapOrgTreeBranchRow(instance: OrgTreeServiceInstance): DashboardBranchRow {
  const resolvedId = instance.id ?? instance.instance_id ?? 'unknown-branch'
  return {
    id: resolvedId,
    name: String(instance.name || instance.title || instance.instance_id || instance.id || 'Unnamed branch'),
    status: instance.status,
    levelVariantName: instance.level_variant_name,
    levelVariantTierCode: instance.tier_code,
    serviceVariantName: instance.service_variant_name
      || instance.service_variant_label
      || instance.service_data?.service_name
      || null,
    branchOwnershipLabel: null,
    isVendorBranch: false,
  }
}

function dedupeBranchRows(rows: DashboardBranchRow[]) {
  const seen = new Set<string>()

  return rows.filter((row) => {
    const key = String(row.id ?? '').trim()
    if (!key || seen.has(key)) {
      return false
    }

    seen.add(key)
    return true
  })
}

function getServiceStatusBadgeClass(label: string) {
  const normalized = label.trim().toLowerCase()
  if (normalized === 'approved') {
    return 'border-transparent bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm shadow-emerald-300/40'
  }

  if (normalized === 'pending' || normalized === 'suspended') {
    return 'border-transparent bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm shadow-amber-300/40'
  }

  if (normalized === 'rejected' || normalized === 'terminated') {
    return 'border-transparent bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-sm shadow-rose-300/40'
  }

  return 'border-border/70 bg-muted/40 text-muted-foreground'
}

function getGroupedCounts<T>(items: T[], resolveKey: (item: T) => string) {
  return items.reduce<Record<string, number>>((accumulator, item) => {
    const key = resolveKey(item)
    accumulator[key] = (accumulator[key] ?? 0) + 1
    return accumulator
  }, {})
}

function formatVariantTypeLabel(value: unknown) {
  const normalized = String(value ?? '').trim()
  return normalized || 'Unknown service type'
}

function humanizeLevelName(raw: string): string {
  // Convert internal uppercase/hyphenated names like "BRANCH", "ROOT-ORG" to "Branch", "Root Org"
  return raw
    .replace(/[-_]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// ── shared sub-chart renderer ──────────────────────────────────────────────
function TrendSubChart({
  data,
  isLoading,
  label,
  barColor,
}: {
  data: MonthlyTrendPoint[]
  isLoading: boolean
  label: string
  barColor: string
}) {
  if (isLoading) return <Skeleton className="h-[200px] w-full rounded-lg" />
  const slug = label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()
  const completedGradientId = `trend-completed-${slug}`
  const cancelledGradientId = `trend-cancelled-${slug}`
  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold text-muted-foreground">{label}</p>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={completedGradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={barColor} stopOpacity={0.4} />
              <stop offset="95%" stopColor={barColor} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id={cancelledGradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#fb7185" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#fb7185" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
          <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={46}
            tickFormatter={(v: number) => `${getCurrencySymbol()}${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
          />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 10, border: '1px solid hsl(var(--border))', boxShadow: '0 8px 24px -12px rgba(15,23,42,0.25)' }}
            formatter={(value: number, name: string) => {
              if (name === 'Revenue') return [`${getCurrencySymbol()}${Number(value).toLocaleString()}`, 'Revenue']
              return [value, name]
            }}
          />
          <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="completed"
            name="Completed"
            stroke={barColor}
            strokeWidth={2.5}
            fill={`url(#${completedGradientId})`}
            dot={false}
            activeDot={{ r: 4.5, strokeWidth: 0 }}
          />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="cancelled"
            name="Cancelled"
            stroke="#fb7185"
            strokeWidth={2}
            fill={`url(#${cancelledGradientId})`}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
          <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 2.5, fill: '#10b981', strokeWidth: 0 }} activeDot={{ r: 4.5 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── main trend card ────────────────────────────────────────────────────────
function DashboardTrendChart({ serviceInstanceId }: { serviceInstanceId: string }) {
  const [range, setRange] = useState<6 | 12 | 'current'>('current')
  const monthsParam = range === 'current' ? 1 : range

  const { data: jobsData = [], isLoading: jobsLoading, isFetching: jobsFetching } = useQuery({
    queryKey: ['dashboard-jobs-trend', serviceInstanceId, range],
    queryFn: () => dashboardApi.getJobsTrend(serviceInstanceId, monthsParam),
    enabled: Boolean(serviceInstanceId),
    staleTime: 5 * 60 * 1000,
  })

  const { data: ordersData = [], isLoading: ordersLoading, isFetching: ordersFetching } = useQuery({
    queryKey: ['dashboard-orders-trend', serviceInstanceId, range],
    queryFn: () => dashboardApi.getOrdersTrend(serviceInstanceId, monthsParam),
    enabled: Boolean(serviceInstanceId),
    staleTime: 5 * 60 * 1000,
  })

  return (
    <section>
      <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
        <CardContent className="p-3 sm:p-4">
          {/* header row */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold text-foreground">Trend — completed, cancelled &amp; revenue</p>
            <div className="flex items-center gap-1 rounded-md border border-border/60 p-0.5">
              {(['current', 6, 12] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`rounded px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                    range === r
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-sm shadow-violet-300/50'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {r === 'current' ? 'Current month' : r === 6 ? 'Last 6 months' : 'Last 1 year'}
                </button>
              ))}
            </div>
          </div>

          {/* two charts side-by-side on md+, stacked on mobile */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <TrendSubChart
              data={jobsData}
              isLoading={jobsLoading || jobsFetching}
              label="Jobs"
              barColor="hsl(var(--primary))"
            />
            <TrendSubChart
              data={ordersData}
              isLoading={ordersLoading || ordersFetching}
              label="Bookings / Orders"
              barColor="#818cf8"
            />
          </div>
        </CardContent>
      </Card>
    </section>
  )
}

type WorkerRiskTrendPoint = {
  month: string
  rejectedAssignments: number
  lateEntries: number
  noShowEntries: number
  workersImpacted: number
}

type OperatingHoursTrendPoint = {
  month: string
  slot_6_9_am: number
  slot_9_12: number
  slot_12_3: number
  slot_3_6: number
  slot_6_9_pm: number
}

type WorkerRiskChartPayload = {
  trendRows: WorkerRiskTrendPoint[]
  operatingHoursRows: OperatingHoursTrendPoint[]
}

const toIsoDateOnly = (date: Date) => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getMonthRangeBuckets = (months = 6) => {
  const now = new Date()

  return Array.from({ length: months }, (_, index) => {
    const offset = months - 1 - index
    const monthStart = new Date(now.getFullYear(), now.getMonth() - offset, 1)
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - offset + 1, 0)
    return {
      label: monthStart.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      start: toIsoDateOnly(monthStart),
      end: toIsoDateOnly(monthEnd),
    }
  })
}

const getCurrentMonthBucket = () => {
  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  return {
    label: monthStart.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
    start: toIsoDateOnly(monthStart),
    end: toIsoDateOnly(now),
  }
}

const isRejectedAssignment = (assignment: Assignment) => {
  const resolved = resolveAssignmentStatus(assignment.status)
  return resolved === 'rejected' || resolved === 'auto_rejected'
}

const isNoShowOrCancelledEntry = (entry: WorkDayEntry) => {
  const statusText = String(entry.status ?? '').trim().toLowerCase()
  const labelText = String(entry.status_label ?? '').trim().toLowerCase()
  const hintText = String(entry.status_hint ?? '').trim().toLowerCase()
  const notesText = String(entry.notes ?? '').trim().toLowerCase()
  const composite = `${statusText} ${labelText} ${hintText} ${notesText}`

  return /\b(cancelled|canceled|no[-_\s]?show)\b/.test(composite)
}

const getWorkerId = (value: unknown) => {
  const workerId = String(value ?? '').trim()
  return workerId || null
}

const OPERATING_HOUR_SLOTS: Array<{
  dataKey: keyof Omit<OperatingHoursTrendPoint, 'month'>
  label: string
  minHour: number
  maxHour: number
  color: string
}> = [
  { dataKey: 'slot_6_9_am', label: '6-9 AM', minHour: 6, maxHour: 9, color: 'hsl(var(--chart-1))' },
  { dataKey: 'slot_9_12', label: '9-12', minHour: 9, maxHour: 12, color: 'hsl(var(--chart-2))' },
  { dataKey: 'slot_12_3', label: '12-3', minHour: 12, maxHour: 15, color: 'hsl(var(--chart-3))' },
  { dataKey: 'slot_3_6', label: '3-6', minHour: 15, maxHour: 18, color: 'hsl(var(--chart-4))' },
  { dataKey: 'slot_6_9_pm', label: '6-9 PM', minHour: 18, maxHour: 21, color: 'hsl(var(--chart-5))' },
]

const getHourFromDateLike = (value: unknown): number | null => {
  const raw = String(value ?? '').trim()
  if (!raw) return null

  const parsed = new Date(raw)
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.getHours()
  }

  const timeMatch = raw.match(/(?:T|\s)(\d{2}):(\d{2})/)
  if (timeMatch) {
    const hour = Number(timeMatch[1])
    return Number.isFinite(hour) ? hour : null
  }

  return null
}

const parseDateLike = (value: unknown): Date | null => {
  const raw = String(value ?? '').trim()
  if (!raw) return null

  const direct = new Date(raw)
  if (!Number.isNaN(direct.getTime())) {
    return direct
  }

  // Fallback for common DD-MM-YYYY or DD/MM/YYYY variants.
  const dmy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/)
  if (dmy) {
    const day = Number(dmy[1])
    const month = Number(dmy[2]) - 1
    const year = Number(dmy[3])
    const hour = Number(dmy[4] ?? 0)
    const minute = Number(dmy[5] ?? 0)
    const parsed = new Date(year, month, day, hour, minute)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed
    }
  }

  return null
}

const toMonthKey = (date: Date): string => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  return `${year}-${month}`
}

function WorkerRiskTrendChart({ serviceInstanceId }: { serviceInstanceId: string }) {
  const labels = useWorkspaceLabels()
  const [range, setRange] = useState<6 | 12 | 'current'>('current')
  const MAX_FETCH_PAGES = 10
  const FETCH_PAGE_SIZE = 500

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['dashboard-worker-risk-trend', serviceInstanceId, range],
    enabled: Boolean(serviceInstanceId),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<WorkerRiskChartPayload> => {
      const queryStartedAt = performance.now()
      const buckets = range === 'current' ? [getCurrentMonthBucket()] : getMonthRangeBuckets(range)
      const lastSixBuckets = range === 'current' ? buckets : getMonthRangeBuckets(6)
      const fromDate = buckets[0]?.start
      const toDate = buckets[buckets.length - 1]?.end

      if (!fromDate || !toDate) {
        return {
          trendRows: [],
          operatingHoursRows: [],
        }
      }

      const monthKeyToLabel = new Map<string, string>()
      buckets.forEach((bucket) => {
        const key = bucket.start.slice(0, 7)
        monthKeyToLabel.set(key, bucket.label)
      })
      const lastSixMonthKeys = new Set<string>(lastSixBuckets.map((bucket) => bucket.start.slice(0, 7)))

      const getMonthKeyFromDateLike = (...values: unknown[]): string | null => {
        for (const value of values) {
          const parsed = parseDateLike(value)
          if (!parsed) continue
          const candidate = toMonthKey(parsed)
          if (monthKeyToLabel.has(candidate)) {
            return candidate
          }
        }
        return null
      }

      const initialByMonth = new Map<string, {
        month: string
        rejectedAssignments: number
        lateEntries: number
        noShowEntries: number
        impactedWorkers: Set<string>
      }>()
      buckets.forEach((bucket) => {
        const key = bucket.start.slice(0, 7)
        initialByMonth.set(key, {
          month: bucket.label,
          rejectedAssignments: 0,
          lateEntries: 0,
          noShowEntries: 0,
          impactedWorkers: new Set<string>(),
        })
      })

      const fetchAssignmentsForRange = async (dateFrom: string, dateTo: string) => {
        let page = 1
        let allRows: Assignment[] = []
        const startedAt = performance.now()

        while (true) {
          const response = await assignmentsApi.getAssignments({
            page,
            page_size: FETCH_PAGE_SIZE,
            date_from: dateFrom,
            date_to: dateTo,
            ordering: 'recently_changed',
          })
          allRows = allRows.concat(response.results)
          if (!response.next || response.results.length < FETCH_PAGE_SIZE || page >= MAX_FETCH_PAGES) {
            if (page >= MAX_FETCH_PAGES && response.next && import.meta.env.DEV) {
              console.warn('[dashboard-risk] assignment fetch capped at max pages', {
                pageCap: MAX_FETCH_PAGES,
                pageSize: FETCH_PAGE_SIZE,
                loadedRows: allRows.length,
                serviceInstanceId,
                range,
              })
            }
            break
          }
          page += 1
        }

        if (import.meta.env.DEV) {
          console.info('[dashboard-risk] assignments fetch timing', {
            elapsedMs: Math.round(performance.now() - startedAt),
            rows: allRows.length,
            pageSize: FETCH_PAGE_SIZE,
            maxPages: MAX_FETCH_PAGES,
            range,
          })
        }

        return allRows
      }

      const fetchWorkDayEntriesForRange = async (dateFrom: string, dateTo: string) => {
        let page = 1
        let allRows: WorkDayEntry[] = []
        const startedAt = performance.now()

        while (true) {
          const response = await assignmentsApi.getWorkDayEntries({
            page,
            page_size: FETCH_PAGE_SIZE,
            date_from: dateFrom,
            date_to: dateTo,
            ordering: 'recently_changed',
          })
          allRows = allRows.concat(response.results)
          if (!response.next || response.results.length < FETCH_PAGE_SIZE || page >= MAX_FETCH_PAGES) {
            if (page >= MAX_FETCH_PAGES && response.next && import.meta.env.DEV) {
              console.warn('[dashboard-risk] workday-entry fetch capped at max pages', {
                pageCap: MAX_FETCH_PAGES,
                pageSize: FETCH_PAGE_SIZE,
                loadedRows: allRows.length,
                serviceInstanceId,
                range,
              })
            }
            break
          }
          page += 1
        }

        if (import.meta.env.DEV) {
          console.info('[dashboard-risk] workday entries fetch timing', {
            elapsedMs: Math.round(performance.now() - startedAt),
            rows: allRows.length,
            pageSize: FETCH_PAGE_SIZE,
            maxPages: MAX_FETCH_PAGES,
            range,
          })
        }

        return allRows
      }

      const [assignments, entries] = await Promise.all([
        fetchAssignmentsForRange(fromDate, toDate),
        fetchWorkDayEntriesForRange(fromDate, toDate),
      ])

      const operatingByMonth = new Map<string, OperatingHoursTrendPoint>()
      lastSixBuckets.forEach((bucket) => {
        const monthKey = bucket.start.slice(0, 7)
        operatingByMonth.set(monthKey, {
          month: bucket.label,
          slot_6_9_am: 0,
          slot_9_12: 0,
          slot_12_3: 0,
          slot_3_6: 0,
          slot_6_9_pm: 0,
        })
      })

      assignments.forEach((assignment) => {
        if (!isRejectedAssignment(assignment)) return
        const monthKey = getMonthKeyFromDateLike(
          assignment.start_date
          ?? assignment.scheduled_start
          ?? assignment.end_date
          ?? assignment.created_at,
        )
        if (!monthKey) return
        const monthRow = initialByMonth.get(monthKey)
        if (!monthRow) return
        monthRow.rejectedAssignments += 1
        const workerId = getWorkerId(assignment.worker_id)
        if (workerId) monthRow.impactedWorkers.add(workerId)
      })

      entries.forEach((entry) => {
        const monthKey = getMonthKeyFromDateLike(
          entry.work_date,
          entry.planned_start_local,
          entry.planned_start,
          entry.check_in_local,
          entry.check_in,
          entry.created_at,
        )
        if (!monthKey) return
        const monthRow = initialByMonth.get(monthKey)
        if (!monthRow) return

        const workerId = getWorkerId(entry.worker)

        if (Boolean(entry.is_late || Number(entry.late_minutes ?? 0) > 0)) {
          monthRow.lateEntries += 1
          if (workerId) monthRow.impactedWorkers.add(workerId)
        }

        if (isNoShowOrCancelledEntry(entry)) {
          monthRow.noShowEntries += 1
          if (workerId) monthRow.impactedWorkers.add(workerId)
        }

        if (lastSixMonthKeys.has(monthKey)) {
          const hour = getHourFromDateLike(
            entry.check_in_local
            ?? entry.check_in
            ?? entry.planned_start_local
            ?? entry.planned_start
            ?? entry.created_at,
          )
          if (hour !== null) {
            const matchingSlot = OPERATING_HOUR_SLOTS.find((slot) => hour >= slot.minHour && hour < slot.maxHour)
            if (matchingSlot) {
              const monthRow = operatingByMonth.get(monthKey)
              if (monthRow) {
                monthRow[matchingSlot.dataKey] += 1
              }
            }
          }
        }
      })

      const trendRows = buckets.map((bucket) => {
        const monthKey = bucket.start.slice(0, 7)
        const row = initialByMonth.get(monthKey)
        return {
          month: bucket.label,
          rejectedAssignments: row?.rejectedAssignments ?? 0,
          lateEntries: row?.lateEntries ?? 0,
          noShowEntries: row?.noShowEntries ?? 0,
          workersImpacted: row?.impactedWorkers.size ?? 0,
        }
      })
      const operatingHoursRows = lastSixBuckets.map((bucket) => {
        const monthKey = bucket.start.slice(0, 7)
        return operatingByMonth.get(monthKey) ?? {
          month: bucket.label,
          slot_6_9_am: 0,
          slot_9_12: 0,
          slot_12_3: 0,
          slot_3_6: 0,
          slot_6_9_pm: 0,
        }
      })

      if (import.meta.env.DEV) {
        console.info('[dashboard-risk] total query timing', {
          elapsedMs: Math.round(performance.now() - queryStartedAt),
          serviceInstanceId,
          range,
        })
      }

      return { trendRows, operatingHoursRows }
    },
  })

  const trendRows = data?.trendRows ?? []
  const operatingHoursRows = data?.operatingHoursRows ?? []

  return (
    <section>
      <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
        <CardHeader className="p-3 sm:p-4 pb-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="space-y-0.5">
              <CardTitle className="text-sm font-semibold text-foreground">Worker risk trend</CardTitle>
            </div>
            <div className="flex items-center gap-1 rounded-md border border-border/60 p-0.5">
              {(['current', 6, 12] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setRange(option)}
                  className={`rounded px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    range === option
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-sm shadow-violet-300/50'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {option === 'current' ? 'Current month' : option === 6 ? 'Last 6 months' : 'Last 1 year'}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          {isLoading || isFetching ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Skeleton className="h-[280px] w-full rounded-lg" />
              <Skeleton className="h-[280px] w-full rounded-lg" />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-border/60 bg-background p-2.5">
                <p className="mb-1 text-sm font-semibold text-foreground">Worker risk trend</p>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={trendRows} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="worker-risk-impacted-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} allowDecimals={false} width={32} />
                    <Tooltip
                      contentStyle={{ fontSize: 13, borderRadius: 10, border: '1px solid hsl(var(--border))', boxShadow: '0 8px 24px -12px rgba(15,23,42,0.25)' }}
                      formatter={(value: number, name: string) => [Number(value), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 13 }} iconSize={8} />
                    <Area type="monotone" dataKey="workersImpacted" name="Workers impacted" stroke="hsl(var(--primary))" strokeWidth={2.75} fill="url(#worker-risk-impacted-gradient)" dot={false} activeDot={{ r: 4.5, strokeWidth: 0 }} />
                    <Line type="monotone" dataKey="rejectedAssignments" name="Assignment rejections" stroke="hsl(var(--destructive))" strokeWidth={2.25} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="lateEntries" name={`Late ${labels.workDayEntryPluralLower}`} stroke="hsl(var(--chart-3))" strokeWidth={2.25} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="noShowEntries" name="No-show / cancelled" stroke="hsl(var(--chart-5))" strokeWidth={2.25} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="border-destructive/30 bg-destructive/5 px-2 py-0.5 text-sm text-destructive">Rejections</Badge>
                  <Badge variant="outline" className="px-2 py-0.5 text-sm" style={{ borderColor: 'hsl(var(--chart-3) / 0.35)', backgroundColor: 'hsl(var(--chart-3) / 0.08)', color: 'hsl(var(--chart-3))' }}>Late</Badge>
                  <Badge variant="outline" className="px-2 py-0.5 text-sm" style={{ borderColor: 'hsl(var(--chart-5) / 0.35)', backgroundColor: 'hsl(var(--chart-5) / 0.08)', color: 'hsl(var(--chart-5))' }}>No-show / cancelled</Badge>
                  <Badge variant="outline" className="border-primary/30 bg-primary/5 px-2 py-0.5 text-sm text-primary">Workers impacted</Badge>
                </div>
              </div>

              <div className="rounded-lg border border-border/60 bg-muted/10 p-2.5">
                <p className="mb-1 text-sm font-semibold text-foreground">
                  Operating hours count ({range === 'current' ? 'current month' : 'last 6 months'})
                </p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={operatingHoursRows} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} interval={0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={28} />
                    <Tooltip
                      contentStyle={{ fontSize: 13, borderRadius: 10, border: '1px solid hsl(var(--border))', boxShadow: '0 8px 24px -12px rgba(15,23,42,0.25)' }}
                      formatter={(value: number, name: string) => [Number(value), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 13 }} iconSize={8} />
                    {OPERATING_HOUR_SLOTS.map((slot) => (
                      <Line
                        key={slot.dataKey}
                        type="monotone"
                        dataKey={slot.dataKey}
                        name={slot.label}
                        stroke={slot.color}
                        strokeWidth={2}
                        connectNulls
                        dot={{ r: 2.2 }}
                        activeDot={{ r: 3.5 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  )
}

export default function DashboardPage() {
  const labels = useWorkspaceLabels()
  const location = useLocation()
  const rawCurrentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const availableBranches = useAuthStore((state) => state.availableBranches)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const accessToken = useAuthStore((state) => state.accessToken)
  const baseServiceInstanceId = resolveActiveServiceInstanceId({
    search: location.search,
    post: rawCurrentPost,
    currentBranch,
    availableBranches,
    storedServiceInstanceId,
    token: accessToken,
  }) || getServiceInstanceIdFromSearch(location.search)
  const currentReturnPath = getLocationReturnPath(location)
  const workspaceNodeId = String(rawCurrentPost?.node_id ?? '').trim()
  const nodeLinkedServiceInstances = useNodeLinkedServiceInstances({
    nodeId: workspaceNodeId,
    activeServiceInstanceId: baseServiceInstanceId,
    enabled: Boolean(workspaceNodeId),
  })
  const serviceInstanceId = useMemo(
    () => resolveWorkspaceActiveServiceInstanceId({
      search: location.search,
      post: rawCurrentPost,
      currentBranch,
      availableBranches,
      storedServiceInstanceId,
      token: accessToken,
      nodeInstanceIds: nodeLinkedServiceInstances.serviceOptions.map((option) => option.instanceId),
    }) || baseServiceInstanceId,
    [
      availableBranches,
      baseServiceInstanceId,
      currentBranch,
      location.search,
      nodeLinkedServiceInstances.serviceOptions,
      rawCurrentPost,
      storedServiceInstanceId,
      accessToken,
    ],
  )
  // Dashboard dates use the tenant timezone once it is known; until then the existing output.
  const dashboardTenantTimeZone = useTenantTimeZone(serviceInstanceId)
  const formatDashboardDay = (value: string) => {
    if (!dashboardTenantTimeZone) return formatDate(value)
    return formatDateOnly(value) ?? formatDate(parseServerDate(value) ?? value, { timeZone: dashboardTenantTimeZone })
  }
  const currentPost = useMemo(() => {
    const scopedPost = resolveScopedPostForServiceInstance({
      currentPost: rawCurrentPost,
      availablePosts,
      serviceInstanceId,
      preferMostCapable: false,
      includeOrganizationScopeFallback: false,
    }) ?? rawCurrentPost

    return resolveWorkspacePostContext({
      post: scopedPost,
      availablePosts,
      serviceInstanceId,
      nodeInstances: nodeLinkedServiceInstances.instances,
    }) ?? scopedPost
  }, [availablePosts, nodeLinkedServiceInstances.instances, rawCurrentPost, serviceInstanceId])
  const defaultCustomRange = useMemo(() => getLastSevenDaysRange(), [])
  const [periodType, setPeriodType] = useState<DashboardPeriodType>('this_month')
  const [customStart, setCustomStart] = useState(defaultCustomRange.start)
  const [customEnd, setCustomEnd] = useState(defaultCustomRange.end)
  const trendsSection = useLazySection('320px')
  const recentSection = useLazySection('260px')
  const orgDirectorySection = useLazySection('220px')
  const shouldLoadTrendSection = trendsSection.isVisible && Boolean(serviceInstanceId)
  const shouldLoadRecentSection = recentSection.isVisible
  const shouldLoadOrgDirectorySection = orgDirectorySection.isVisible

  const handlePeriodTypeChange = (value: DashboardPeriodType) => {
    setPeriodType(value)
    if (value === 'custom') {
      const range = getLastSevenDaysRange()
      setCustomStart(range.start)
      setCustomEnd(range.end)
    }
  }
  const currentCapabilities = currentPost?.capabilities
  const currentVariantUniqueId = String(currentPost?.service_variant_unique_id ?? '').trim()
  const currentBusinessType = String(currentPost?.service_variant_name || currentPost?.service_variant_type || '')
    .trim()
    .toLowerCase()
  const isOrgRoleContext = currentVariantUniqueId
    ? isOrganizationServiceVariant(currentVariantUniqueId, null)
    : isOrganizationServiceVariant(null, currentBusinessType)
  const canViewTenantDirectory = Boolean(currentCapabilities?.can_view_tenant_directory)
  const canViewUserDirectory = Boolean(currentCapabilities?.can_view_user_directory)
  const canViewRoleDirectory = Boolean(currentCapabilities?.can_view_role_directory)
  const canManageRoles = Boolean(currentCapabilities?.can_manage_roles)
  const canAccessRoles = Boolean(
    currentCapabilities?.can_list_role_assignments || currentCapabilities?.can_manage_roles,
  )
  const canAccessPath = (path: string) => evaluateRouteAccess(path, currentPost).authorized
  const canAccessHref = (href?: string | null) => {
    if (!href) return false
    const [pathname] = href.split('?')
    return canAccessPath(pathname || href)
  }
  const canAccessDashboardPath = (path: string) => canAccessPath(path)
  const canAccessDashboardHref = (href?: string | null) => {
    if (!href) return false
    return canAccessHref(href)
  }
  const canAccessJobs = canAccessDashboardPath('/job')
  const canCreateJobs = canAccessDashboardPath('/job/create')
  const canAccessWorkers = canAccessDashboardPath('/workers')
  const canAccessAssignments = canAccessDashboardPath('/assignments')
  const canAccessWorkdays = canAccessDashboardPath('/workdays')
  const canAccessExpenses = canAccessDashboardPath('/job/expenses') || canAccessDashboardPath('/finance/expenses')
  const canAccessInvoices = canAccessDashboardPath('/finance/invoices')
  const canAccessWorkerPayments = canAccessDashboardPath('/job/worker/payments') || canAccessDashboardPath('/finance/worker-payments')
  const filterAccessibleLinks = <T extends { href: string }>(items: T[]) => items.filter((item) => canAccessPath(item.href))
  const adminWorkspaceHref = isBranchOperatorWorkspaceContextFromPost(currentPost)
    ? ORG_BRANCH_WORKSPACE_PATH
    : ORG_ORGANIZATION_PAGE_PATH
  const organizationTreeSetupQuickLink = useMemo(() => {
    const variantId = currentPost?.service_variant_unique_id
    const variantName = currentPost?.service_variant_name ?? currentPost?.service_variant_type
    const title = getOrganizationTreeSetupLabel(
      variantId,
      variantName,
      currentPost?.level_variant_name,
    )
    if (isTenantOrganizationServiceVariant(variantId, variantName)) {
      return {
        title,
        description: 'Configure tenant settings, structure, vendors, and branches.',
      }
    }
    if (isBranchOperatorWorkspaceContextFromPost(currentPost)) {
      return {
        title,
        description: 'Configure branch settings, structure, vendors, and branches.',
      }
    }
    return {
      title,
      description: 'Configure organization settings, structure, vendors, and branches.',
    }
  }, [
    currentPost,
    currentPost?.level_variant_name,
    currentPost?.service_variant_name,
    currentPost?.service_variant_type,
    currentPost?.service_variant_unique_id,
  ])
  const orgStructureLabel = useMemo(() => {
    const lv = String(currentPost?.level_variant_name ?? '').trim()
    return lv || 'Organization'
  }, [currentPost?.level_variant_name])
  const formatDashboardAmount = (amount: number | null | undefined) => {
    const numericAmount = Number(amount ?? 0)
    return formatCurrency(Number.isFinite(numericAmount) ? numericAmount : 0)
  }
  const orgStructureLabelLower = orgStructureLabel.toLowerCase()
  const branchesDirectoryListHref = useMemo(() => {
    const params = new URLSearchParams()
    params.set('lvl_variant', 'branch')
    params.set('unique_level_variant_id', BRANCH_CATALOG_LEVEL_VARIANT_UNIQUE_ID)
    return `${ORG_SERVICE_INSTANCE_LIST_BASE}?${params.toString()}`
  }, [])

  const { data: overview, isLoading: overviewLoading, isFetching: overviewFetching } = useQuery({
    queryKey: ['dashboard-overview', serviceInstanceId, periodType, customStart, customEnd],
    queryFn: () => dashboardApi.getOverview({
      type: periodType,
      start: periodType === 'custom' ? customStart : undefined,
      end: periodType === 'custom' ? customEnd : undefined,
    }),
    enabled: !isOrgRoleContext,
  })

  const { data: jobs, isLoading: jobsLoading, isFetching: jobsFetching } = useQuery({
    queryKey: ['jobs-summary', serviceInstanceId],
    queryFn: () => jobsApi.getJobs({ page_size: 5 }),
    enabled: !isOrgRoleContext && canAccessDashboardPath('/job') && shouldLoadRecentSection,
  })

  const { data: workers, isLoading: workersLoading, isFetching: workersFetching } = useQuery({
    queryKey: ['workers-summary', serviceInstanceId],
    queryFn: () => workersApi.getWorkers({ page_size: 5 }),
    enabled: !isOrgRoleContext && canAccessDashboardPath('/workers') && shouldLoadRecentSection,
  })

  const { data: branchInstances, isLoading: branchesLoading, isFetching: branchesFetching } = useQuery({
    queryKey: ['org-dashboard-branches', serviceInstanceId, BRANCH_CATALOG_LEVEL_VARIANT_UNIQUE_ID],
    queryFn: () => orgTreeApi.listServiceInstancesByWorkspaceInstanceAndLevelVariant(
      serviceInstanceId!,
      BRANCH_CATALOG_LEVEL_VARIANT_UNIQUE_ID,
    ),
    staleTime: 60_000,
    enabled: isOrgRoleContext && Boolean(serviceInstanceId),
  })

  const { data: tenants, isLoading: tenantsLoading, isFetching: tenantsFetching } = useQuery({
    queryKey: ['org-dashboard-tenants', serviceInstanceId],
    queryFn: () => tenantsApi.list({ serviceInstanceId, uniqueLevelVariantId: 'tenant-lvl-variant' }),
    staleTime: 60_000,
    enabled: isOrgRoleContext && canViewTenantDirectory && shouldLoadOrgDirectorySection,
  })

  const { data: clients, isLoading: clientsLoading, isFetching: clientsFetching } = useQuery({
    queryKey: ['org-dashboard-clients', serviceInstanceId],
    queryFn: () => orgUsersApi.listClients({ includeInactive: true, serviceInstanceId }),
    staleTime: 60_000,
    enabled: isOrgRoleContext && shouldLoadOrgDirectorySection,
  })

  const { data: suppliers, isLoading: suppliersLoading, isFetching: suppliersFetching } = useQuery({
    queryKey: ['org-dashboard-suppliers', serviceInstanceId],
    queryFn: () => orgUsersApi.listSuppliers({ includeInactive: true, serviceInstanceId }),
    staleTime: 60_000,
    enabled: isOrgRoleContext && shouldLoadOrgDirectorySection,
  })

  const { data: orgUsers, isLoading: orgUsersLoading, isFetching: orgUsersFetching } = useQuery({
    queryKey: ['org-dashboard-users', serviceInstanceId],
    queryFn: () => orgUsersApi.list({ includeInactive: true, pageSize: 1000, serviceInstanceId }),
    staleTime: 60_000,
    enabled: isOrgRoleContext && canViewUserDirectory && shouldLoadOrgDirectorySection,
  })

  const { data: roles, isLoading: rolesLoading, isFetching: rolesFetching } = useQuery({
    queryKey: ['org-dashboard-roles', serviceInstanceId],
    queryFn: () => orgRolesApi.list({ serviceInstanceId }),
    staleTime: 60_000,
    enabled: isOrgRoleContext && canAccessRoles && shouldLoadOrgDirectorySection,
  })

  const workerRows = workers?.results ?? []
  const jobRows = jobs?.results ?? []
  const isLoading = jobsLoading || workersLoading || overviewLoading
  const isDashboardRefreshing = jobsFetching || workersFetching || overviewFetching
  const isDashboardBusy = isLoading || isDashboardRefreshing

  if (isOrgRoleContext) {
    const branchRows = dedupeBranchRows((branchInstances ?? []).map(mapOrgTreeBranchRow))
    const tenantRows = tenants ?? []
    const clientRows = clients ?? []
    const supplierRows = suppliers ?? []
    const orgUserRows = orgUsers ?? []
    const roleRows = roles ?? []
    const orgDashboardLoading = branchesLoading
      || tenantsLoading
      || clientsLoading
      || suppliersLoading
      || orgUsersLoading
      || rolesLoading
    const orgDashboardRefreshing = branchesFetching
      || tenantsFetching
      || clientsFetching
      || suppliersFetching
      || orgUsersFetching
      || rolesFetching
    const orgDashboardBusy = orgDashboardLoading || orgDashboardRefreshing
    const effectiveBranchRowsForStatus: Array<{ status?: unknown }> = branchRows
    const effectiveTenantRowsForStatus: Array<{ status?: unknown }> = tenantRows
    const activeClientCount = clientRows.filter((client) => client.is_active !== false).length
    const activeSupplierCount = supplierRows.filter((supplier) => supplier.is_active !== false).length
    const activeOrgUserCount = orgUserRows.filter((user) => user.is_active !== false && user.deleted !== true).length
    const branchStatusCounts = getGroupedCounts(effectiveBranchRowsForStatus, (branch) => normalizeServiceStatus(branch.status))
    const tenantStatusCounts = getGroupedCounts(effectiveTenantRowsForStatus, (tenant) => normalizeServiceStatus(tenant.status))
    const userRegistrationCounts = getGroupedCounts(orgUserRows, (user) => getRegistrationStatusLabel(user.registration_status ?? null))
    const pendingTenantCount = effectiveTenantRowsForStatus.filter((tenant) => normalizeServiceStatus(tenant.status) === 'Pending').length
    const pendingBranchCount = effectiveBranchRowsForStatus.filter((branch) => normalizeServiceStatus(branch.status) === 'Pending').length
    const pendingUserCount = orgUserRows.filter((user) => Number(user.registration_status ?? -1) === 1).length
    const branchVariantCounts = Object.entries(
      getGroupedCounts(
        branchRows.filter(isApprovedServiceInstance),
        (branch) => formatVariantTypeLabel(branch.serviceVariantName),
      ),
    )
      .map(([label, count]) => ({ label, count }))
      .sort((left, right) => right.count - left.count)

    // Build child level stats from branch + tenant API responses, grouped by level variant name.
    type ChildRow = {
      levelVariantName?: string | null
      levelVariantTierCode?: string | null
      status?: string | number | null
      isActive?: boolean
    }
    const getChildRowLabel = (row: ChildRow) => {
      const name = String(row.levelVariantName ?? '').trim()
      if (name) return humanizeLevelName(name)
      const tierCode = String(row.levelVariantTierCode ?? '').trim()
      if (tierCode) return humanizeLevelName(tierCode)
      return 'Child services'
    }
    const buildChildLevelStats = (
      rows: ChildRow[],
      icon: typeof GitBranch,
      fallbackTitle: string,
      href: string,
    ) => {
      if (rows.length === 0) {
        return []
      }

      const grouped = getGroupedCounts(rows, (row) => {
        const label = getChildRowLabel(row)
        return label === 'Child services' ? fallbackTitle : label
      })

      return Object.entries(grouped).map(([label, totalCount]) => {
        const matchingRows = rows.filter((row) => {
          const resolvedLabel = getChildRowLabel(row)
          const normalizedLabel = resolvedLabel === 'Child services' ? fallbackTitle : resolvedLabel
          return normalizedLabel === label
        })
        const activeCount = matchingRows.filter(isApprovedServiceInstance).length

        return {
          title: label,
          value: activeCount,
          caption: totalCount === activeCount
            ? `${activeCount} active`
            : `${activeCount} active · ${totalCount} total`,
          icon,
          href,
        }
      })
    }
    const dynamicChildLevelStats = [
      ...buildChildLevelStats(
        branchRows.map((branch) => ({
          levelVariantName: branch.levelVariantName,
          levelVariantTierCode: branch.levelVariantTierCode,
          status: branch.status,
          isActive: branch.isActive,
        })),
        GitBranch,
        'Service branches',
        branchesDirectoryListHref,
      ),
      ...buildChildLevelStats(
        tenantRows.map((tenant) => ({
          levelVariantName: tenant.levelVariantName,
          levelVariantTierCode: tenant.levelVariantTierCode,
          status: tenant.status,
        })),
        Landmark,
        'Tenants',
        '/org/tenants?unique_level_variant_id=tenant-lvl-variant',
      ),
    ].sort((left, right) => Number(right.value) - Number(left.value))
    const childStatsCards = dynamicChildLevelStats.map((item, index) => ({
      ...item,
      ...CHILD_STAT_CARD_TONES[index % CHILD_STAT_CARD_TONES.length],
    }))
    const largestVariantCount = branchVariantCounts[0]?.count ?? 0
    const businessName = currentPost?.org_name || currentBranch?.name || 'Organization context'
    const businessType = currentPost?.service_variant_name || 'Organization'
    const pendingAlerts = [
      pendingTenantCount > 0
        ? { key: 'tenants', title: 'Pending tenants', description: `${pendingTenantCount} tenant${pendingTenantCount === 1 ? '' : 's'} waiting for approval`, href: '/org/tenants?unique_level_variant_id=tenant-lvl-variant', icon: Landmark }
        : null,
      pendingBranchCount > 0
        ? { key: 'branches', title: 'Pending branches', description: `${pendingBranchCount} branch${pendingBranchCount === 1 ? '' : 'es'} waiting for approval`, href: '/org/service-instance', icon: GitBranch }
        : null,
      pendingUserCount > 0
        ? { key: 'users', title: 'Pending users', description: `${pendingUserCount} user${pendingUserCount === 1 ? '' : 's'} waiting for approval`, href: '/org/users', icon: Users }
        : null,
    ].filter((item): item is { key: string; title: string; description: string; href: string; icon: typeof Landmark } => Boolean(item))
    const quickLinks = [
      { title: organizationTreeSetupQuickLink.title, description: organizationTreeSetupQuickLink.description, href: adminWorkspaceHref, icon: FolderTree },
      { title: 'Manage Branches', description: `Create and review service branches under this ${orgStructureLabelLower}.`, href: ORG_SERVICE_INSTANCE_LIST_BASE, icon: GitBranch },
      { title: 'Review Vendors', description: 'View the active vendor directory for this organization.', href: '/org/users?user_group_label=vendor', icon: Handshake },
      ...(canViewTenantDirectory
        ? [{ title: 'Tenants', description: 'Review organization child instances and grant role access to them.', href: '/org/tenants?unique_level_variant_id=tenant-lvl-variant', icon: Landmark }]
        : []),
      ...(canViewUserDirectory
        ? [{ title: 'Users', description: 'Review organization users, branch scope, and registration state.', href: '/org/users', icon: Users }]
        : []),
      ...(canViewRoleDirectory && canAccessRoles
        ? [{ title: 'Roles', description: 'Review assigned roles and open role management when updates are needed.', href: '/org/roles', icon: Settings }]
        : []),
      ...(canViewRoleDirectory && canManageRoles
        ? [{ title: 'Features', description: 'Maintain the service feature catalog and sidebar mapping keys from the browser.', href: '/org/features', icon: KeyRound }]
        : []),
    ]
    const visibleQuickLinks = filterAccessibleLinks(quickLinks)
    const recentBranches = branchRows.filter(isApprovedServiceInstance).slice(0, 5)

    return (
      <div className="space-y-3 pb-2">
        <section>
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-4 shadow-lg shadow-violet-500/20 sm:p-5">
            <div className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 left-1/3 h-56 w-56 rounded-full bg-fuchsia-400/20 blur-3xl" />
            <div className="relative flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <div className="max-w-2xl space-y-1.5">
                <Badge variant="outline" className="w-fit rounded-full border-white/30 bg-white/15 px-2.5 py-0 text-[11px] font-medium text-white backdrop-blur-sm">
                  {`${orgStructureLabel} overview`}
                </Badge>
                <div className="space-y-0.5">
                  <h1 className="text-xl font-bold tracking-tight text-white sm:text-2xl">{`${orgStructureLabel} dashboard`}</h1>
                  <p className="max-w-2xl text-[11px] leading-4 text-white/80 sm:text-xs">
                    {`Track the ${orgStructureLabelLower} structure, active service branches, and admin readiness without loading job-management operations into the ${orgStructureLabelLower} workspace.`}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 backdrop-blur-sm xl:min-w-[220px]">
                <p className="text-[10px] uppercase tracking-[0.14em] text-white/70">{`Active ${orgStructureLabelLower}`}</p>
                <p className="mt-0.5 text-sm font-semibold text-white">{businessName}</p>
                <p className="mt-0.5 text-[11px] text-white/80">{`${orgStructureLabel} type: ${businessType}`}</p>
              </div>
            </div>
          </div>

          <Card className="mt-2 rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardContent className="space-y-3 p-3 sm:p-4">
              {orgDashboardBusy ? <DashboardStatSkeletonGrid /> : (
              <div className="grid gap-1.5 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {[
                  ...childStatsCards,
                  {
                    title: 'Active vendors',
                    value: activeClientCount,
                    caption: `${clientRows.length} total vendor records in this ${orgStructureLabelLower}`,
                    icon: Handshake,
                    href: '/org/users?user_group_label=vendor',
                    panelTone: 'border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-teal-50',
                    iconTone: 'bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-sm shadow-emerald-300/50',
                    labelTone: 'text-emerald-700',
                    captionTone: 'text-emerald-800/80',
                  },
                  {
                    title: 'Suppliers',
                    value: activeSupplierCount,
                    caption: `${supplierRows.length} supplier records in the org scope`,
                    icon: ShieldCheck,
                    href: '/org/users?user_group_label=supplier',
                    panelTone: 'border-rose-200/70 bg-gradient-to-br from-rose-50 to-pink-50',
                    iconTone: 'bg-gradient-to-br from-rose-400 to-pink-500 text-white shadow-sm shadow-rose-300/50',
                    labelTone: 'text-rose-700',
                    captionTone: 'text-rose-800/80',
                  },
                  {
                    title: 'Org users',
                    value: canViewUserDirectory ? activeOrgUserCount : 'Locked',
                    caption: canViewUserDirectory
                      ? `${orgUserRows.length} total users, ${pendingUserCount} pending approvals`
                      : 'User directory access is required to load org users',
                    icon: Users,
                    href: canViewUserDirectory ? '/org/users' : undefined,
                    panelTone: 'border-indigo-200/70 bg-gradient-to-br from-indigo-50 to-blue-50',
                    iconTone: 'bg-gradient-to-br from-indigo-500 to-blue-500 text-white shadow-sm shadow-indigo-300/50',
                    labelTone: 'text-indigo-700',
                    captionTone: 'text-indigo-800/80',
                  },
                  {
                    title: 'Roles',
                    value: canAccessRoles ? roleRows.length : 0,
                    caption: canAccessRoles ? 'Organization roles available for access control' : 'Role access is not available for this organization role',
                    icon: Settings,
                    href: canAccessRoles ? '/org/roles' : undefined,
                    panelTone: 'border-fuchsia-200/70 bg-gradient-to-br from-fuchsia-50 to-purple-50',
                    iconTone: 'bg-gradient-to-br from-fuchsia-500 to-purple-500 text-white shadow-sm shadow-fuchsia-300/50',
                    labelTone: 'text-fuchsia-700',
                    captionTone: 'text-fuchsia-800/80',
                  },
                ].map((stat) => {
                  const statHref = stat.href && canAccessHref(stat.href)
                    ? withServiceInstanceId(stat.href, serviceInstanceId)
                    : null
                  const cardClassName = `rounded-xl border px-2 py-1.5 shadow-sm transition-all duration-200 ${stat.panelTone} ${statHref ? 'hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60' : ''}`
                  const content = (
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-0.5">
                        <p className={`truncate text-xs font-medium ${stat.labelTone}`}>{stat.title}</p>
                        <p className="text-lg font-bold text-foreground">{stat.value}</p>
                        {stat.caption ? (
                          <p className={`text-[10px] leading-4 ${stat.captionTone ?? 'text-muted-foreground'}`}>{stat.caption}</p>
                        ) : null}
                      </div>
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${stat.iconTone}`}>
                        <stat.icon className="h-4 w-4" />
                      </div>
                    </div>
                  )

                  return statHref ? (
                    <Link key={stat.title} to={statHref} className={cardClassName}>
                      {content}
                    </Link>
                  ) : (
                    <div key={stat.title} className={cardClassName}>
                      {content}
                    </div>
                  )
                })}
              </div>
              )}
            </CardContent>
          </Card>
        </section>

        <section ref={orgDirectorySection.ref} className="grid gap-2 xl:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.9fr)]">
          <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="p-3 pb-1.5">
              <CardTitle className="text-sm">Quick actions</CardTitle>
              <CardDescription className="text-[11px]">Jump into the main organization administration areas for this organization role.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-1.5 p-3 pt-0 sm:grid-cols-2">
              {visibleQuickLinks.map((item) => (
                <Link key={item.href} to={withServiceInstanceId(item.href, serviceInstanceId)} className="group rounded-lg border border-border/70 bg-background px-2.5 py-2 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md">
                  <div className="flex items-start gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-sm shadow-indigo-300/40 transition-transform duration-200 group-hover:scale-110">
                      <item.icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-foreground">{item.title}</p>
                      <p className="text-[11px] text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
              {visibleQuickLinks.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 px-4 py-6 text-center sm:col-span-2">
                  <p className="text-sm font-medium text-foreground">No quick actions available</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">This role does not currently expose organization shortcut pages.</p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="p-3 pb-1.5">
              <CardTitle className="text-sm">Attention needed</CardTitle>
              <CardDescription className="text-[11px]">Pending approvals and high-level organization posture for the current organization role.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5 p-3 pt-0">
              {orgDashboardBusy ? (
                <DashboardListSkeleton count={3} height="h-16" />
              ) : pendingAlerts.length > 0 ? (
                <div className="rounded-lg border border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 px-2.5 py-2.5">
                  <div className="flex items-start gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm shadow-amber-300/50">
                      <AlertCircle className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div>
                        <p className="text-xs font-medium text-amber-950">Pending approvals need attention</p>
                        <p className="text-[11px] text-amber-900/80">Review blocked tenants, branches, or users before they stall onboarding.</p>
                      </div>
                      <div className="space-y-1">
                        {pendingAlerts.map((item) => (
                          <Link
                            key={item.key}
                            to={withServiceInstanceId(item.href, serviceInstanceId)}
                            className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-white/80 px-2 py-1.5 transition-colors hover:bg-white"
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <div className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-100 text-amber-700">
                                <item.icon className="h-3 w-3" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-foreground">{item.title}</p>
                                <p className="text-[11px] text-muted-foreground">{item.description}</p>
                              </div>
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-amber-700" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-teal-50 px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-sm shadow-emerald-300/50">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-emerald-950">No pending approvals</p>
                      <p className="text-[11px] text-emerald-900/80">Tenants, branches, and org users are currently clear for this workspace.</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-border/70 bg-background px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-violet-500 to-indigo-500 text-white shadow-sm shadow-violet-300/40">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-foreground">Organization role active</p>
                    <p className="text-[11px] text-muted-foreground">Job-management metrics are hidden here so each service variant can have its own dashboard experience.</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-border/70 bg-background px-3 py-2.5">
                <p className="text-xs font-medium text-foreground">{`Current ${orgStructureLabelLower} context`}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{currentBranch?.name || 'Organization context'}</p>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="grid gap-2 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="p-3 pb-1.5">
              <CardTitle className="text-sm">Branch mix by service type</CardTitle>
              <CardDescription className="text-[11px]">Color-coded view of service variant distribution across branches.</CardDescription>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {orgDashboardBusy ? (
                <DashboardListSkeleton count={4} height="h-10" />
              ) : branchVariantCounts.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 px-4 py-6 text-center">
                  <p className="text-sm font-medium text-foreground">No branch service data yet</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Service-variant distribution will appear once branches are available.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {branchVariantCounts.map((item, index) => {
                    const width = largestVariantCount > 0 ? Math.max(16, Math.round((item.count / largestVariantCount) * 100)) : 0
                    return (
                      <div key={item.label} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-foreground">{item.label}</p>
                            <p className="text-[11px] text-muted-foreground">{item.count} branch{item.count === 1 ? '' : 'es'}</p>
                          </div>
                          <Badge variant="outline" className="rounded-md px-1.5 py-0 text-[10px]">{item.count}</Badge>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-muted/70">
                          <div className={`h-full rounded-full ${GRAPH_TONES[index % GRAPH_TONES.length]}`} style={{ width: `${width}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="p-3 pb-1.5">
              <CardTitle className="text-sm">Status breakdown</CardTitle>
              <CardDescription className="text-[11px]">Approval pipeline snapshot for tenants, branches, and org users.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-3 pt-0">
              {orgDashboardBusy ? (
                <DashboardListSkeleton count={3} height="h-9" />
              ) : (
              <>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-foreground">Tenants</p>
                  {!canViewTenantDirectory ? <Badge variant="outline" className="px-1.5 py-0 text-[10px]">Locked</Badge> : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {canViewTenantDirectory ? SERVICE_STATUS_ORDER.map((label) => (
                    <Badge key={`tenant-${label}`} variant="outline" className={`px-1.5 py-0 text-[10px] ${getServiceStatusBadgeClass(label)}`}>
                      {label}: {tenantStatusCounts[label] ?? 0}
                    </Badge>
                  )) : <p className="text-xs text-muted-foreground">Tenant directory access is required to show tenant status counts.</p>}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">Branches</p>
                <div className="flex flex-wrap gap-1.5">
                  {SERVICE_STATUS_ORDER.map((label) => (
                    <Badge key={`branch-${label}`} variant="outline" className={`px-1.5 py-0 text-[10px] ${getServiceStatusBadgeClass(label)}`}>
                      {label}: {branchStatusCounts[label] ?? 0}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-foreground">Users</p>
                  {!canViewUserDirectory ? <Badge variant="outline" className="px-1.5 py-0 text-[10px]">Locked</Badge> : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {canViewUserDirectory ? Object.entries(userRegistrationCounts)
                    .sort((left, right) => left[0].localeCompare(right[0]))
                    .map(([label, count]) => {
                      const matchingValue = [0, 1, 2, 3, 4, 5].find((value) => getRegistrationStatusLabel(value) === label) ?? null
                      return (
                        <Badge key={`user-${label}`} variant="outline" className={`px-1.5 py-0 text-[10px] ${getRegistrationStatusBadgeClass(matchingValue)}`}>
                          {label}: {count}
                        </Badge>
                      )
                    }) : <p className="text-xs text-muted-foreground">User directory access is required to show org-user status counts.</p>}
                </div>
              </div>
              </>
              )}
            </CardContent>
          </Card>
        </section>

        <section>
          <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="p-3 pb-1.5">
              <CardTitle className="text-sm">Recent service branches</CardTitle>
              <CardDescription className="text-[11px]">{`Latest branches available under the current ${orgStructureLabelLower}.`}</CardDescription>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {orgDashboardBusy ? (
                <DashboardListSkeleton count={3} height="h-12" />
              ) : recentBranches.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 px-4 py-6 text-center">
                  <p className="text-sm font-medium text-foreground">No branches yet</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{`Create a branch to start structuring this ${orgStructureLabelLower}.`}</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {recentBranches.map((branch) => (
                    <div key={String(branch.id)} className="flex items-start justify-between gap-3 rounded-lg border border-border/70 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground">{branch.name}</p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">{branch.serviceVariantName || 'Unknown service type'}</p>
                      </div>
                      <Badge variant="outline" className="px-1.5 py-0 text-[10px]">{branch.branchOwnershipLabel || (branch.isVendorBranch ? 'Vendor Branch' : 'Own Branch')}</Badge>
                    </div>
                  ))}
                  {canAccessPath('/org/service-instance') ? (
                    <Button variant="outline" size="sm" className="h-7 rounded-md text-xs" asChild>
                      <Link to={withServiceInstanceId(branchesDirectoryListHref, serviceInstanceId)}>
                        Open branches
                        <ArrowRight className="ml-1.5 h-3 w-3" />
                      </Link>
                    </Button>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    )
  }

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const actionShortcuts = [
    { title: 'Create job', href: '/job/create', icon: Briefcase },
    { title: 'Review workers', href: '/workers', icon: Users },
    { title: 'Open assignments', href: '/assignments', icon: ClipboardList },
    { title: 'Review expenses', href: '/job/expenses', icon: DollarSign },
    { title: 'Review worker payouts', href: '/job/worker/payments', icon: CircleDollarSign },
  ].filter((item) => canAccessDashboardHref(item.href))
  const period = overview?.period
  const periodLabel = period?.type === 'custom' && period.start && period.end
    ? `${formatDashboardDay(period.start)} to ${formatDashboardDay(period.end)}`
    : period?.label ?? 'Today'

  const getWorkerName = (worker: { display_name?: string; full_name?: string; email?: string; id?: string | number }) => {
    const workerRegistrationNumber = String((worker as { user_registration_number?: string | null }).user_registration_number ?? '').trim()
    return worker.display_name || worker.full_name || workerRegistrationNumber || `Worker ${worker.id ?? ''}`.trim() || 'Unknown Worker'
  }

  const getWorkerStatusBadge = (status?: string) => {
    if (status === 'active') {
      return <Badge variant="outline" className="border-transparent bg-gradient-to-r from-emerald-500 to-teal-500 px-1.5 py-0 text-[11px] text-white shadow-sm shadow-emerald-300/40">Active</Badge>
    }

    if (status === 'pending') {
      return <Badge variant="outline" className="border-transparent bg-gradient-to-r from-amber-500 to-orange-500 px-1.5 py-0 text-[11px] text-white shadow-sm shadow-amber-300/40">Pending</Badge>
    }

    return <Badge variant="outline" className="border-slate-200 bg-slate-50 px-1.5 py-0 text-[11px] text-slate-700">Inactive</Badge>
  }

  const getJobStatusBadge = (status?: string) => {
    const classes: Record<string, string> = {
      draft: 'border-slate-200 bg-slate-50 text-slate-700',
      open: 'border-transparent bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow-sm shadow-blue-300/40',
      in_progress: 'border-transparent bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm shadow-amber-300/40',
      completed: 'border-transparent bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm shadow-emerald-300/40',
      cancelled: 'border-transparent bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-sm shadow-rose-300/40',
      on_hold: 'border-transparent bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm shadow-orange-300/40',
    }

    const normalizedStatus = status || 'draft'

    return (
      <Badge variant="outline" className={`${classes[normalizedStatus] || classes.draft} px-1.5 py-0 text-[11px]`}>
        {normalizedStatus.replace(/_/g, ' ')}
      </Badge>
    )
  }

  const statCards = [
    {
      title: `${periodLabel} jobs`,
      value: overview?.summary?.jobs_count ?? 0,
      caption: 'Scheduled workload in the selected window',
      icon: Briefcase,
      tone: 'bg-gradient-to-br from-violet-500 to-indigo-500 text-white shadow-sm shadow-violet-300/50',
      panelTone: 'border-violet-200/70 bg-gradient-to-br from-violet-50 to-indigo-50',
      href: '/job',
    },
    {
      title: 'Workers needed',
      value: overview?.summary?.workers_needed_count ?? 0,
      caption: `${overview?.summary?.unique_workers_count ?? 0} distinct workers currently allocated`,
      icon: Users,
      tone: 'bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-sm shadow-emerald-300/50',
      panelTone: 'border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-teal-50',
      href: '/assignments',
    },
    {
      title: 'Active workers',
      value: overview?.summary?.active_workers_count ?? 0,
      caption: 'Approved workers registered for this branch',
      icon: Users,
      tone: 'bg-gradient-to-br from-sky-400 to-blue-500 text-white shadow-sm shadow-sky-300/50',
      panelTone: 'border-sky-200/70 bg-gradient-to-br from-sky-50 to-blue-50',
      href: '/workers',
    },
    {
      title: 'Online workers',
      value: overview?.summary?.online_workers_count ?? 0,
      caption: 'Workers currently checked in',
      icon: CircleDot,
      tone: 'bg-gradient-to-br from-teal-400 to-cyan-500 text-white shadow-sm shadow-teal-300/50',
      panelTone: 'border-teal-200/70 bg-gradient-to-br from-teal-50 to-cyan-50',
      href: '/workers',
    },
    {
      title: 'In progress',
      value: overview?.summary?.in_progress_jobs_count ?? 0,
      caption: 'Jobs already on the ground',
      icon: ClipboardList,
      tone: 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm shadow-amber-300/50',
      panelTone: 'border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50',
      href: '/job?status=in_progress',
    },
    {
      title: 'Completed',
      value: overview?.summary?.completed_jobs_count ?? 0,
      caption: 'Delivered in the selected window',
      icon: CheckCircle2,
      tone: 'bg-gradient-to-br from-emerald-400 to-green-500 text-white shadow-sm shadow-emerald-300/50',
      panelTone: 'border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-green-50',
      href: '/job?status=completed',
    },
    {
      title: 'Cancelled',
      value: overview?.summary?.cancelled_jobs_count ?? 0,
      caption: 'Watch for capacity or client churn issues',
      icon: Ban,
      tone: 'bg-gradient-to-br from-rose-400 to-pink-500 text-white shadow-sm shadow-rose-300/50',
      panelTone: 'border-rose-200/70 bg-gradient-to-br from-rose-50 to-pink-50',
      href: '/job?status=cancelled',
    },
    {
      title: 'Next 7 days',
      value: overview?.summary?.next_7_days_jobs_count ?? 0,
      caption: `${overview?.summary?.next_7_days_workers_needed_count ?? 0} worker slots to plan`,
      icon: CalendarClock,
      tone: 'bg-gradient-to-br from-indigo-400 to-purple-500 text-white shadow-sm shadow-indigo-300/50',
      panelTone: 'border-indigo-200/70 bg-gradient-to-br from-indigo-50 to-purple-50',
      href: '/workdays',
    },
  ]

  const headlineCards = [
    statCards[0],
    statCards[1],
    statCards[2],
    statCards[3],
    {
      title: 'Revenue',
      value: formatDashboardAmount(overview?.financials?.projected_revenue ?? 0),
      caption: 'Expected value inside the selected window',
      icon: DollarSign,
      tone: 'bg-gradient-to-br from-emerald-400 to-green-500 text-white shadow-sm shadow-emerald-300/50',
      panelTone: 'border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-green-50',
      href: '/finance/invoices',
    },
    {
      title: 'Open invoices',
      value: formatDashboardAmount(overview?.financials?.pending_invoices_total ?? 0),
      caption: `${overview?.financials?.pending_invoices_count ?? 0} customer invoices still open`,
      icon: FileClock,
      tone: 'bg-gradient-to-br from-violet-400 to-purple-500 text-white shadow-sm shadow-violet-300/50',
      panelTone: 'border-violet-200/70 bg-gradient-to-br from-violet-50 to-purple-50',
      href: '/finance/invoices',
    },
    {
      title: 'Unpaid due',
      value: formatDashboardAmount(overview?.financials?.pending_orders_total ?? 0),
      caption: 'Orders placed but not yet paid',
      icon: DollarSign,
      tone: 'bg-gradient-to-br from-rose-400 to-red-500 text-white shadow-sm shadow-rose-300/50',
      panelTone: 'border-rose-200/70 bg-gradient-to-br from-rose-50 to-red-50',
    },
  ]

  const workflowCards = [statCards[4], statCards[5], statCards[6], statCards[7]]

  const alertItems = [
    {
      title: 'Unassigned slots',
      value: overview?.summary?.unassigned_assignments_count ?? 0,
      valueLabel: String(overview?.summary?.unassigned_assignments_count ?? 0),
      description: 'assignments still need a worker',
      icon: AlertCircle,
      tone: 'border-orange-200/70 bg-gradient-to-br from-orange-50 to-amber-50 text-orange-800',
      targetId: 'dashboard-unassigned-assignments-panel',
      href: '/assignments?unassigned=true',
    },
    {
      title: `${labels.workDayEntry} attention`,
      value: overview?.summary?.attention_work_day_entries_count ?? 0,
      valueLabel: String(overview?.summary?.attention_work_day_entries_count ?? 0),
      description: `${labels.workDayEntryPluralLower} flagged for review`,
      icon: CalendarClock,
      tone: 'border-rose-200/70 bg-gradient-to-br from-rose-50 to-pink-50 text-rose-800',
      targetId: 'dashboard-attention-work-day-entries-panel',
      href: '/workdays',
    },
    {
      title: `Late ${labels.workDayEntryPluralLower}`,
      value: overview?.summary?.late_work_day_entries_count ?? 0,
      valueLabel: String(overview?.summary?.late_work_day_entries_count ?? 0),
      description: 'workers delayed or checked in late',
      icon: Clock,
      tone: 'border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-800',
      targetId: 'dashboard-late-work-day-entries-panel',
      href: '/workdays',
    },
    {
      title: 'No-shows',
      value: overview?.summary?.no_show_work_day_entries_count ?? 0,
      valueLabel: String(overview?.summary?.no_show_work_day_entries_count ?? 0),
      description: 'scheduled workers who did not check in',
      icon: UserX,
      tone: 'border-rose-200/70 bg-gradient-to-br from-rose-50 to-pink-50 text-rose-800',
      targetId: 'dashboard-no-show-work-day-entries-panel',
      href: '/workdays',
    },
    {
      title: 'Upcoming schedules',
      value: overview?.summary?.next_7_days_workday_schedules_count ?? 0,
      valueLabel: String(overview?.summary?.next_7_days_workday_schedules_count ?? 0),
      description: 'work day schedules next 7 days',
      icon: CalendarRange,
      tone: 'border-sky-200/70 bg-gradient-to-br from-sky-50 to-blue-50 text-sky-800',
      targetId: 'dashboard-upcoming-schedules-panel',
      href: '/workdays',
    },
    {
      title: 'Clashed schedules',
      value: overview?.summary?.clashed_schedules_count ?? 0,
      valueLabel: String(overview?.summary?.clashed_schedules_count ?? 0),
      description: 'worker schedule overlaps',
      icon: AlertTriangle,
      tone: 'border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-800',
      targetId: 'dashboard-clashed-schedules-panel',
      href: '/workdays',
    },
    {
      title: 'Expenses due',
      value: overview?.financials?.upcoming_expenses_total ?? 0,
      valueLabel: formatDashboardAmount(overview?.financials?.upcoming_expenses_total ?? 0),
      description: 'likely in the next 7 days',
      icon: ReceiptText,
      tone: 'border-rose-200/70 bg-gradient-to-br from-rose-50 to-pink-50 text-rose-800',
      targetId: 'dashboard-expenses-panel',
      href: '/finance/expenses',
    },
    {
      title: 'Worker payouts',
      value: overview?.financials?.pending_worker_payments_total ?? 0,
      valueLabel: formatDashboardAmount(overview?.financials?.pending_worker_payments_total ?? 0),
      description: `due across ${overview?.financials?.pending_worker_periods_count ?? 0} payout periods`,
      icon: Wallet,
      tone: 'border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-800',
      targetId: 'dashboard-worker-payments-panel',
      href: '/job/worker/payments',
    },
    {
      title: 'Open invoices',
      value: overview?.financials?.pending_invoices_total ?? 0,
      valueLabel: formatDashboardAmount(overview?.financials?.pending_invoices_total ?? 0),
      description: `across ${overview?.financials?.pending_invoices_count ?? 0} customer invoices`,
      icon: FileClock,
      tone: 'border-violet-200/70 bg-gradient-to-br from-violet-50 to-purple-50 text-violet-800',
      targetId: 'dashboard-pending-invoices-panel',
      href: '/finance/invoices',
    },
    {
      title: 'Unpaid orders',
      value: overview?.financials?.pending_orders_total ?? 0,
      valueLabel: formatDashboardAmount(overview?.financials?.pending_orders_total ?? 0),
      description: 'orders placed but not yet paid',
      icon: DollarSign,
      tone: 'border-rose-200/70 bg-gradient-to-br from-rose-50 to-pink-50 text-rose-800',
      targetId: undefined,
      href: undefined,
    },
  ]

  const financeCards = [
    {
      title: 'Upcoming expenses',
      totalLabel: 'Next 7 days',
      totalValue: formatDashboardAmount(overview?.financials?.upcoming_expenses_total ?? 0),
      description: 'Purchases and unpaid costs likely to hit cash flow soon.',
      icon: ReceiptText,
      tone: 'border-rose-200/70 bg-gradient-to-br from-rose-50 to-pink-50 text-rose-800',
      href: '/finance/expenses',
    },
    {
      title: 'Worker payouts',
      totalLabel: 'Selected window',
      totalValue: formatDashboardAmount(overview?.financials?.pending_worker_payments_total ?? 0),
      description: 'Pending payout periods that need cash planning.',
      icon: Wallet,
      tone: 'border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-800',
      href: '/job/worker/payments',
    },
    {
      title: 'Customer invoices',
      totalLabel: 'Open balance',
      totalValue: formatDashboardAmount(overview?.financials?.pending_invoices_total ?? 0),
      description: 'Draft and issued invoices still waiting to be sent or collected.',
      icon: FileClock,
      tone: 'border-violet-200/70 bg-gradient-to-br from-violet-50 to-purple-50 text-violet-800',
      href: '/finance/invoices',
    },
  ]
  const visibleHeadlineCards = headlineCards.filter((card) => canAccessDashboardHref(card.href))
  const visibleWorkflowCards = workflowCards.filter((card) => canAccessDashboardHref(card.href))
  const visibleAlertItems = alertItems.filter((item) => !item.href || canAccessDashboardHref(item.href))
  const visibleFinanceCards = financeCards.filter((card) => canAccessDashboardHref(card.href))

  const isPastDue = (dateStr?: string | null) => {
    if (!dateStr) return false
    // Tenant calendar days once the tenant timezone is known; until then the browser day as before.
    const dayKey = toTenantDayKey(dateStr, dashboardTenantTimeZone)
    const todayKey = dashboardTenantTimeZone ? toTenantDateKey(new Date(), dashboardTenantTimeZone) : null
    if (dayKey && todayKey) return dayKey < todayKey
    return new Date(dateStr) < new Date(new Date().toDateString())
  }

  // shared card wrapper — entire card is a Link
  const itemCardCls = (overdue: boolean, borderColor: string, bgColor: string, overdueColor = 'border-l-rose-500 bg-gradient-to-r from-rose-50 to-pink-50') =>
    `block rounded-lg border border-border/60 border-l-4 px-3 py-2 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 ${overdue ? overdueColor : `${borderColor} ${bgColor}`}`

  const renderExpenseItem = (item: DashboardExpenseWatchItem) => {
    const overdue = isPastDue(item.due_date)
    const href = withServiceInstanceId(
      item.job_id ? `/job/expenses?job_id=${encodeURIComponent(String(item.job_id))}` : '/job/expenses',
      serviceInstanceId,
    )
    return (
      <Link key={item.id} to={href} className={itemCardCls(overdue, 'border-l-orange-400', 'bg-gradient-to-r from-orange-50 to-amber-50/60')}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="truncate text-xs font-semibold text-foreground">{item.title}</p>
              <Badge variant="outline" className="px-1 py-0 text-[9px] capitalize">{item.source_type}</Badge>
              {overdue && <span className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1 py-0 text-[9px] font-semibold text-rose-700"><CalendarX className="h-2.5 w-2.5" />Past due</span>}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              <p className="truncate text-[11px] text-muted-foreground">
                {item.job_id ? formatJobTitleWithId({ jobId: item.job_id, title: item.job_title }) : 'No job linked'}
              </p>
              {item.job_ref ? <Badge variant="outline" className="shrink-0 px-1 py-0 text-[9px]">{item.job_ref}</Badge> : null}
            </div>
            <p className={`mt-0.5 text-[10px] ${overdue ? 'font-medium text-rose-600' : 'text-muted-foreground'}`}>
              {item.due_date ? `Due ${formatDashboardDay(item.due_date)}` : 'No due date'}
              {item.secondary_label ? ` · ${item.secondary_label}` : ''}
            </p>
          </div>
          <p className={`shrink-0 text-xs font-bold ${overdue ? 'text-rose-600' : 'text-foreground'}`}>{formatDashboardAmount(item.amount)}</p>
        </div>
      </Link>
    )
  }

  const renderWorkerPaymentItem = (item: DashboardWorkerPaymentWatchItem) => {
    const overdue = isPastDue(item.period_end)
    const href = withServiceInstanceId(
      `/job/worker/payments?worker_id=${encodeURIComponent(String(item.worker_id))}`,
      serviceInstanceId,
    )
    return (
      <Link key={item.id} to={href} className={itemCardCls(overdue, 'border-l-amber-400', 'bg-gradient-to-r from-amber-50 to-yellow-50/60')}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="truncate text-xs font-semibold text-foreground">{item.worker_name}</p>
              {overdue && <span className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1 py-0 text-[9px] font-semibold text-rose-700"><CalendarX className="h-2.5 w-2.5" />Past due</span>}
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {item.period_start && item.period_end
                ? `${formatDashboardDay(item.period_start)} → ${formatDashboardDay(item.period_end)}`
                : 'Payment period pending'}
            </p>
            <p className="mt-0.5 text-[10px] text-muted-foreground">{item.jobs_count} jobs in this payout period</p>
          </div>
          <p className={`shrink-0 text-xs font-bold ${overdue ? 'text-rose-600' : 'text-foreground'}`}>{formatDashboardAmount(item.amount_due)}</p>
        </div>
      </Link>
    )
  }

  const renderPendingInvoiceItem = (item: DashboardPendingInvoiceItem) => {
    const overdue = isPastDue(item.due_date)
    const href = withServiceInstanceId(
      item.job_id ? `/finance/invoices/${encodeURIComponent(String(item.job_id))}` : '/finance/invoices',
      serviceInstanceId,
    )
    return (
      <Link key={item.id} to={href} className={itemCardCls(overdue, 'border-l-violet-400', 'bg-gradient-to-r from-violet-50 to-purple-50/60')}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="truncate text-xs font-semibold text-foreground">{item.invoice_number}</p>
              <Badge variant="outline" className="px-1 py-0 text-[9px] capitalize">{item.status}</Badge>
              {overdue && <span className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1 py-0 text-[9px] font-semibold text-rose-700"><CalendarX className="h-2.5 w-2.5" />Overdue</span>}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              <p className="truncate text-[11px] text-muted-foreground">
                {item.client_name || 'No client'}
                {item.job_id ? ` · ${formatJobTitleWithId({ jobId: item.job_id, title: item.job_title })}` : ''}
              </p>
              {item.job_ref ? <Badge variant="outline" className="shrink-0 px-1 py-0 text-[9px]">{item.job_ref}</Badge> : null}
            </div>
            <p className={`mt-0.5 text-[10px] ${overdue ? 'font-medium text-rose-600' : 'text-muted-foreground'}`}>
              {item.due_date ? `Due ${formatDashboardDay(item.due_date)}` : item.issued_at ? `Issued ${formatDashboardDay(item.issued_at)}` : 'No due date'}
            </p>
          </div>
          <p className={`shrink-0 text-xs font-bold ${overdue ? 'text-rose-600' : 'text-foreground'}`}>{formatDashboardAmount(item.amount_due)}</p>
        </div>
      </Link>
    )
  }

  const renderUnassignedAssignmentItem = (item: DashboardUnassignedAssignmentItem) => {
    const scheduledSoon = item.scheduled_start && !isPastDue(item.scheduled_start)
      && (new Date(item.scheduled_start).getTime() - Date.now()) < 3 * 24 * 60 * 60 * 1000
    const pastSchedule = isPastDue(item.scheduled_start)
    const href = withServiceInstanceId(`/assignments/${item.id}`, serviceInstanceId)
    return (
      <Link key={item.id} to={href} className={`block rounded-lg border border-border/60 border-l-4 px-3 py-2 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 ${pastSchedule ? 'border-l-rose-500 bg-gradient-to-r from-rose-50 to-pink-50/60' : scheduledSoon ? 'border-l-amber-400 bg-gradient-to-r from-amber-50 to-yellow-50/60' : 'border-l-orange-400 bg-gradient-to-r from-orange-50 to-amber-50/40'}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="truncate text-xs font-semibold text-foreground">
                {item.job_title || formatJobTitleWithId({ jobId: item.job_id, title: item.job_title, jobRef: item.job_ref })}
              </p>
              {item.job_ref && <Badge variant="outline" className="px-1 py-0 text-[9px]">{item.job_ref}</Badge>}
              {pastSchedule && <span className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1 py-0 text-[9px] font-semibold text-rose-700"><AlertCircle className="h-2.5 w-2.5" />Overdue</span>}
              {scheduledSoon && !pastSchedule && <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0 text-[9px] font-semibold text-amber-700"><Clock className="h-2.5 w-2.5" />Soon</span>}
            </div>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{item.role_label || 'Role not specified'}</p>
            <p className={`mt-0.5 text-[10px] ${pastSchedule ? 'font-medium text-rose-600' : 'text-muted-foreground'}`}>
              {item.scheduled_start ? `Scheduled ${formatDashboardDay(item.scheduled_start)}` : 'No schedule set'}
              {item.requirement_slot ? ` · Slot ${item.requirement_slot}` : ''}
            </p>
          </div>
          <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
        </div>
      </Link>
    )
  }

  const renderAttentionWorkDayEntryItem = (item: DashboardAttentionWorkDayEntryItem) => (
    <Link key={item.id} to={withReturnTo(withServiceInstanceId(`/workdays/${item.id}/edit`, serviceInstanceId), currentReturnPath)} className="block rounded-lg border border-border/60 border-l-4 border-l-rose-500 bg-gradient-to-r from-rose-50 to-pink-50/60 px-3 py-2 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-xs font-semibold text-foreground">
              {item.job_title || formatJobTitleWithId({ jobId: item.job_id, title: item.job_title })}
            </p>
            {item.job_ref && <Badge variant="outline" className="px-1 py-0 text-[9px]">{item.job_ref}</Badge>}
            <span className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1 py-0 text-[9px] font-semibold text-rose-700"><AlertTriangle className="h-2.5 w-2.5" />Attention</span>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{item.worker_name || 'Unknown worker'}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {item.work_date ? `Work date ${formatDashboardDay(item.work_date)}` : 'No work date'}
            {item.status ? ` · ${item.status.replace(/_/g, ' ')}` : ''}
            {item.notes ? ` · ${item.notes}` : ''}
          </p>
        </div>
        <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
      </div>
    </Link>
  )

  const renderUpcomingWorkdayScheduleItem = (item: DashboardUpcomingWorkdayScheduleItem) => (
    <Link key={item.id} to={withReturnTo(withServiceInstanceId(`/workdays/${item.id}/edit`, serviceInstanceId), currentReturnPath)} className="block rounded-lg border border-border/60 border-l-4 border-l-sky-400 bg-gradient-to-r from-sky-50 to-blue-50/60 px-3 py-2 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-xs font-semibold text-foreground">
              {item.job_title || formatJobTitleWithId({ jobId: item.job_id, title: item.job_title })}
            </p>
            {item.job_ref && <Badge variant="outline" className="px-1 py-0 text-[9px]">{item.job_ref}</Badge>}
            <Badge variant="outline" className="border-transparent bg-gradient-to-r from-sky-500 to-blue-500 px-1 py-0 text-[9px] text-white">Upcoming</Badge>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{item.worker_name || 'Unknown worker'}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {item.work_date ? `Work date ${formatDashboardDay(item.work_date)}` : 'No work date'}
            {item.status ? ` · ${item.status.replace(/_/g, ' ')}` : ''}
          </p>
        </div>
        <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
      </div>
    </Link>
  )

  const renderClashedScheduleItem = (item: DashboardClashedScheduleItem) => (
    <Link key={item.id} to={withReturnTo(withServiceInstanceId(`/workdays/${item.id}/edit`, serviceInstanceId), currentReturnPath)} className="block rounded-lg border border-border/60 border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 to-orange-50/60 px-3 py-2 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-xs font-semibold text-foreground">
              {item.job_title || formatJobTitleWithId({ jobId: item.job_id, title: item.job_title })}
            </p>
            {item.job_ref && <Badge variant="outline" className="px-1 py-0 text-[9px]">{item.job_ref}</Badge>}
            <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0 text-[9px] font-semibold text-amber-700"><AlertTriangle className="h-2.5 w-2.5" />{item.overlap_conflict_count ?? 0} clash{(item.overlap_conflict_count ?? 0) === 1 ? '' : 'es'}</span>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{item.worker_name || 'Unknown worker'}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {item.work_date ? `Work date ${formatDashboardDay(item.work_date)}` : 'No work date'}
            {item.status ? ` · ${item.status.replace(/_/g, ' ')}` : ''}
            {item.notes ? ` · ${item.notes}` : ''}
          </p>
        </div>
        <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
      </div>
    </Link>
  )

  return (
    <div className="space-y-3 pb-2">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 px-4 py-3 shadow-lg shadow-violet-500/20 sm:px-5 sm:py-4">
        <div className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 left-1/3 h-56 w-56 rounded-full bg-fuchsia-400/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-2">
          <div>
            <Badge variant="outline" className="w-fit rounded-full border-white/30 bg-white/15 px-2.5 py-0 text-[11px] font-medium text-white backdrop-blur-sm">
              Operations dashboard
            </Badge>
            <h1 className="mt-1 text-xl font-bold tracking-tight text-white sm:text-2xl">{`${periodLabel} overview`}</h1>
            <p className="mt-0.5 text-[11px] text-white/80 sm:text-xs">{currentBranch?.name || 'Track jobs, workers, and cash flow in one place.'}</p>
          </div>
        </div>
      </div>
      {canAccessWorkdays ? (
        <WorkDayAttendanceAlerts
          lateCount={overview?.summary?.late_work_day_entries_count ?? 0}
          noShowCount={overview?.summary?.no_show_work_day_entries_count ?? 0}
          lateItems={overview?.late_work_day_entries ?? []}
          noShowItems={overview?.no_show_work_day_entries ?? []}
          isLoading={isDashboardBusy}
          serviceInstanceId={serviceInstanceId}
          returnPath={currentReturnPath}
        />
      ) : null}
      <section>
        <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
          <CardContent className="space-y-2 p-3 sm:p-4">

            {/* ── Top bar: period picker + count badges + action buttons on one line ── */}
            <div className="flex flex-wrap items-center gap-1.5">
              {/* Period picker – compact inline */}
              <div className="flex items-center gap-1 rounded-lg border border-violet-200/70 bg-gradient-to-r from-violet-50 to-indigo-50 px-2 py-1">
                <CalendarRange className="h-3.5 w-3.5 shrink-0 text-violet-600" />
                <Select value={periodType} onValueChange={(value) => handlePeriodTypeChange(value as DashboardPeriodType)}>
                  <SelectTrigger className="h-7 w-[120px] border-0 bg-transparent p-0 text-xs shadow-none focus:ring-0 [&>svg]:hidden">
                    <SelectValue placeholder="Period" />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIOD_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {periodType === 'custom' ? (
                  <>
                    <span className="text-[11px] text-muted-foreground">from</span>
                    <Input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} className="h-7 w-[120px] border-0 bg-transparent p-0 text-xs shadow-none focus-visible:ring-0" />
                    <span className="text-[11px] text-muted-foreground">to</span>
                    <Input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} className="h-7 w-[120px] border-0 bg-transparent p-0 text-xs shadow-none focus-visible:ring-0" />
                  </>
                ) : (
                  <span className="text-[11px] font-medium text-foreground">{periodLabel}</span>
                )}
              </div>

              {/* Count stat badges inline */}
              {canAccessAssignments ? (
                <UnassignedAssignmentsAttention
                  count={overview?.summary?.unassigned_assignments_count ?? 0}
                  isLoading={isDashboardBusy}
                  serviceInstanceId={serviceInstanceId}
                  timeZone={dashboardTenantTimeZone}
                />
              ) : null}
              {canAccessWorkdays ? (
                <>
                  <WorkDayEntriesAttention
                    count={overview?.summary?.attention_work_day_entries_count ?? 0}
                    isLoading={isDashboardBusy}
                    serviceInstanceId={serviceInstanceId}
                  timeZone={dashboardTenantTimeZone}
                  />
                  <Badge variant="outline" className="border-transparent bg-gradient-to-r from-sky-500 to-blue-500 text-[11px] text-white shadow-sm shadow-sky-300/40">
                    {overview?.summary?.next_7_days_workday_schedules_count ?? 0} upcoming
                  </Badge>
                  <Badge variant="outline" className="border-transparent bg-gradient-to-r from-amber-500 to-orange-500 text-[11px] text-white shadow-sm shadow-amber-300/40">
                    {overview?.summary?.late_work_day_entries_count ?? 0} late
                  </Badge>
                  <Badge variant="outline" className="border-transparent bg-gradient-to-r from-rose-500 to-pink-500 text-[11px] text-white shadow-sm shadow-rose-300/40">
                    {overview?.summary?.no_show_work_day_entries_count ?? 0} no-show
                  </Badge>
                  <Badge variant="outline" className="border-transparent bg-gradient-to-r from-amber-500 to-orange-500 text-[11px] text-white shadow-sm shadow-amber-300/40">
                    {overview?.summary?.clashed_schedules_count ?? 0} clashed
                  </Badge>
                </>
              ) : null}

              {/* Action buttons pushed to the right */}
              <div className="ml-auto flex shrink-0 flex-wrap gap-1.5">
                {canAccessWorkerPayments ? (
                  <Button variant="outline" size="sm" className="h-7 rounded-md text-xs" asChild>
                    <Link to={withServiceInstanceId('/job/worker/payments', serviceInstanceId)}>Payouts</Link>
                  </Button>
                ) : null}
                {canCreateJobs ? (
                  <Button size="sm" className="h-7 rounded-md bg-gradient-to-r from-violet-600 to-indigo-600 text-xs shadow-sm shadow-violet-300/50 hover:from-violet-500 hover:to-indigo-500" asChild>
                    <Link to={withServiceInstanceId('/job/create', serviceInstanceId)}>+ Job</Link>
                  </Button>
                ) : null}
              </div>
            </div>

            {/* ── Headline stat cards with per-card colour variations ── */}
            {visibleHeadlineCards.length > 0 ? (
              isDashboardBusy ? <DashboardStatSkeletonGrid count={visibleHeadlineCards.length} /> : (
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 xl:grid-cols-6">
                {visibleHeadlineCards.map((stat) => {
                const statHref = stat.href && canAccessDashboardHref(stat.href)
                  ? withServiceInstanceId(stat.href, serviceInstanceId)
                  : null
                const panelTone = (stat as { panelTone?: string }).panelTone ?? 'border-border/70 bg-background'
                const cardClassName = `rounded-lg border px-2 py-1.5 shadow-sm transition-all duration-200 ${panelTone} ${statHref ? 'hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60' : ''}`
                const content = (
                  <div className="flex items-start justify-between gap-1.5">
                    <div className="min-w-0 space-y-0.5">
                      <p className="truncate text-[10px] leading-tight text-muted-foreground">{stat.title}</p>
                      <p className="text-base font-bold leading-tight text-foreground">{stat.value}</p>
                    </div>
                    <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${stat.tone}`}>
                      <stat.icon className="h-3 w-3" />
                    </div>
                  </div>
                )

                return statHref ? (
                  <Link key={stat.title} to={statHref} className={cardClassName}>
                    {content}
                  </Link>
                ) : (
                  <div key={stat.title} className={cardClassName}>
                    {content}
                  </div>
                )
                })}
              </div>
              )
            ) : null}

            {/* ── Operational flow circles ── */}
            {visibleWorkflowCards.length > 0 ? (
              <div className="border-t border-border/50 pt-2">
                <div className="flex flex-wrap items-center gap-x-0.5 gap-y-0">
                  <p className="mr-2 shrink-0 text-[10px] font-semibold text-muted-foreground">Operational flow</p>
                  {visibleWorkflowCards.map((stat) => {
                    const statHref = stat.href && canAccessDashboardHref(stat.href)
                      ? withServiceInstanceId(stat.href, serviceInstanceId)
                      : null
                    const inner = (
                      <div className="flex items-center gap-2 rounded-md px-2 py-1">
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-110 ${stat.tone}`}>
                          {isDashboardBusy
                            ? <Skeleton className="h-4 w-4 rounded-full bg-current/20" />
                            : <span className="text-xs font-bold leading-none">{stat.value}</span>
                          }
                        </div>
                        <p className="text-[10px] font-medium leading-tight text-muted-foreground">{stat.title}</p>
                      </div>
                    )
                    return statHref ? (
                      <Link key={stat.title} to={statHref} className="group rounded-md transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60">
                        {inner}
                      </Link>
                    ) : (
                      <div key={stat.title}>{inner}</div>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </section>

      {/* ── 6-month trend chart ── */}
      <div ref={trendsSection.ref}>
        {shouldLoadTrendSection && serviceInstanceId ? (
          <>
            <DashboardTrendChart serviceInstanceId={serviceInstanceId} />
            <WorkerRiskTrendChart serviceInstanceId={serviceInstanceId} />
          </>
        ) : serviceInstanceId ? (
          <div className="space-y-2">
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        ) : null}
      </div>

      {visibleAlertItems.length > 0 || canAccessAssignments || canAccessWorkdays ? (
      <section className="space-y-1.5">

        {visibleAlertItems.length > 0 || canAccessWorkdays ? (
        <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
          <CardHeader className="space-y-0 px-2.5 py-1.5">
            <CardTitle className="text-xs">Management watch</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-1 p-2 pt-0 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {visibleAlertItems.map((item) => {
              const inner = (
                <>
                  <div className="flex items-center justify-between gap-1">
                    <item.icon className="h-3 w-3 shrink-0 opacity-60" />
                    {item.targetId && <ArrowRight className="h-2.5 w-2.5 shrink-0 opacity-40" />}
                  </div>
                  {isDashboardBusy ? <Skeleton className="mt-1.5 h-5 w-14" /> : <p className="mt-1.5 text-lg font-bold leading-none tracking-tight">{item.valueLabel}</p>}
                  <p className="mt-0.5 text-[10px] font-semibold leading-tight">{item.title}</p>
                  <p className="mt-0.5 text-[9px] leading-relaxed opacity-70 break-words">{item.description}</p>
                </>
              )
              const cls = `flex h-full min-w-0 flex-col rounded-md border px-2 py-1.5 shadow-sm transition-all duration-200 ${item.tone} ${item.targetId || item.href ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60' : ''}`
              // If item has a navigable href, render as Link; otherwise scroll to panel
              const navHref = item.href && canAccessDashboardHref(item.href)
                ? withServiceInstanceId(item.href, serviceInstanceId)
                : null
              return navHref ? (
                <Link key={item.title} to={navHref} className={cls}>
                  {inner}
                </Link>
              ) : item.targetId ? (
                <button key={item.title} type="button" onClick={() => scrollToSection(item.targetId!)} className={cls}>
                  {inner}
                </button>
              ) : (
                <div key={item.title} className={cls}>{inner}</div>
              )
            })}
            {canAccessJobs || canAccessWorkdays ? (
            <div className="flex h-full min-w-0 flex-col rounded-md border border-indigo-200/70 bg-gradient-to-br from-indigo-50 to-violet-50 px-2 py-1.5 text-indigo-800 shadow-sm">
              <div className="flex items-center justify-between gap-1">
                <CalendarClock className="h-3 w-3 shrink-0 opacity-60" />
              </div>
              {isDashboardBusy ? <Skeleton className="mt-1.5 h-5 w-14" /> : (
                <p className="mt-1.5 text-lg font-bold leading-none tracking-tight">
                  {overview?.summary?.next_7_days_jobs_count ?? 0}
                </p>
              )}
              <p className="mt-0.5 text-[10px] font-semibold leading-tight">Next 7 days</p>
              <p className="mt-0.5 line-clamp-2 text-[9px] opacity-60">
                {overview?.summary?.next_7_days_workers_needed_count ?? 0} worker slots on the near-term schedule
              </p>
            </div>
            ) : null}
          </CardContent>
        </Card>
        ) : null}

        {canAccessAssignments || canAccessWorkdays ? (
        <div className="grid gap-2 lg:grid-cols-2">
          {canAccessAssignments ? (
          <Card id="dashboard-unassigned-assignments-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
              <div>
                <CardTitle className="text-sm">Unassigned slots</CardTitle>
                <CardDescription className="text-[11px]">Active job slots waiting for a worker in the selected period.</CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1">
                <UnassignedAssignmentsAttention
                  count={overview?.summary?.unassigned_assignments_count ?? 0}
                  isLoading={isDashboardBusy}
                  serviceInstanceId={serviceInstanceId}
                  timeZone={dashboardTenantTimeZone}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 p-2.5">
              {isDashboardBusy ? (
                <DashboardListSkeleton count={4} height="h-14" />
              ) : (overview?.unassigned_assignments?.length ?? 0) > 0 ? (
                <div className="space-y-1.5">
                  {(overview?.unassigned_assignments ?? []).map(renderUnassignedAssignmentItem)}
                </div>
              ) : (
                <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                  <ClipboardList className="h-8 w-8 opacity-60" />
                  <div>
                    <p className="text-sm font-medium text-foreground">All slots assigned</p>
                    <p className="mt-0.5 text-xs">Open assignment slots without workers will appear here.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          ) : null}

          {canAccessWorkdays ? (
          <>
          <Card id="dashboard-attention-work-day-entries-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
              <div>
                <CardTitle className="text-sm">Attention {labels.workDayEntryPluralLower}</CardTitle>
                <CardDescription className="text-[11px]">Flagged {labels.workDayEntryPluralLower} waiting for supervisor review in the selected period.</CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1">
                <WorkDayEntriesAttention
                  count={overview?.summary?.attention_work_day_entries_count ?? 0}
                  isLoading={isDashboardBusy}
                  serviceInstanceId={serviceInstanceId}
                  timeZone={dashboardTenantTimeZone}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 p-2.5">
              {isDashboardBusy ? (
                <DashboardListSkeleton count={4} height="h-14" />
              ) : (overview?.attention_work_day_entries?.length ?? 0) > 0 ? (
                <div className="space-y-1.5">
                  {(overview?.attention_work_day_entries ?? []).map(renderAttentionWorkDayEntryItem)}
                </div>
              ) : (
                <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                  <CalendarClock className="h-8 w-8 opacity-60" />
                  <div>
                    <p className="text-sm font-medium text-foreground">No attention {labels.workDayEntryPluralLower}</p>
                    <p className="mt-0.5 text-xs">Flagged {labels.workDayEntryPluralLower} will appear here.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card id="dashboard-late-work-day-entries-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <Collapsible defaultOpen={false} className="group">
              <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
                <CollapsibleTrigger asChild>
                  <button type="button" className="flex min-w-0 flex-1 items-start justify-between gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60">
                    <div className="min-w-0">
                      <CardTitle className="text-sm">Late {labels.workDayEntryPluralLower}</CardTitle>
                      <CardDescription className="text-[11px]">Workers who are delayed or checked in after the planned start.</CardDescription>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[11px] text-amber-800">
                        {overview?.summary?.late_work_day_entries_count ?? 0} late
                      </Badge>
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                    </div>
                  </button>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                <CardContent className="space-y-1.5 p-2.5">
                  {isDashboardBusy ? (
                    <DashboardListSkeleton count={4} height="h-14" />
                  ) : (overview?.late_work_day_entries?.length ?? 0) > 0 ? (
                    <GroupedAttendanceAlertList
                      items={overview?.late_work_day_entries ?? []}
                      tone="late"
                      serviceInstanceId={serviceInstanceId}
                      returnPath={currentReturnPath}
                    />
                  ) : (
                    <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                      <Clock className="h-8 w-8 opacity-60" />
                      <div>
                        <p className="text-sm font-medium text-foreground">No late {labels.workDayEntryPluralLower}</p>
                        <p className="mt-0.5 text-xs">Delayed {labels.workDayEntryPluralLower} will appear here.</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          <Card id="dashboard-no-show-work-day-entries-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <Collapsible defaultOpen={false} className="group">
              <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
                <CollapsibleTrigger asChild>
                  <button type="button" className="flex min-w-0 flex-1 items-start justify-between gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60">
                    <div className="min-w-0">
                      <CardTitle className="text-sm">No-show {labels.workDayEntryPluralLower}</CardTitle>
                      <CardDescription className="text-[11px]">Scheduled workers who missed check-in after the planned window.</CardDescription>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Badge variant="outline" className="border-rose-300 bg-rose-50 text-[11px] text-rose-800">
                        {overview?.summary?.no_show_work_day_entries_count ?? 0} no-show
                      </Badge>
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                    </div>
                  </button>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                <CardContent className="space-y-1.5 p-2.5">
                  {isDashboardBusy ? (
                    <DashboardListSkeleton count={4} height="h-14" />
                  ) : (overview?.no_show_work_day_entries?.length ?? 0) > 0 ? (
                    <GroupedAttendanceAlertList
                      items={overview?.no_show_work_day_entries ?? []}
                      tone="no_show"
                      serviceInstanceId={serviceInstanceId}
                      returnPath={currentReturnPath}
                    />
                  ) : (
                    <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                      <UserX className="h-8 w-8 opacity-60" />
                      <div>
                        <p className="text-sm font-medium text-foreground">No no-shows</p>
                        <p className="mt-0.5 text-xs">Missed check-ins will appear here.</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          <Card id="dashboard-upcoming-schedules-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
              <div>
                <CardTitle className="text-sm">Upcoming work day schedules</CardTitle>
                <CardDescription className="text-[11px]">Next 7-day worker schedules from the work day calendar.</CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge variant="outline" className="border-sky-300 bg-sky-50 text-[11px] text-sky-800">
                  {overview?.summary?.next_7_days_workday_schedules_count ?? 0} schedule{(overview?.summary?.next_7_days_workday_schedules_count ?? 0) === 1 ? '' : 's'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 p-2.5">
              {isDashboardBusy ? (
                <DashboardListSkeleton count={4} height="h-14" />
              ) : (overview?.upcoming_workday_schedules?.length ?? 0) > 0 ? (
                <div className="space-y-1.5">
                  {(overview?.upcoming_workday_schedules ?? []).map(renderUpcomingWorkdayScheduleItem)}
                </div>
              ) : (
                <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                  <CalendarRange className="h-8 w-8 opacity-60" />
                  <div>
                    <p className="text-sm font-medium text-foreground">No upcoming schedules</p>
                    <p className="mt-0.5 text-xs">Upcoming work day schedules will appear here.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card id="dashboard-clashed-schedules-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
              <div>
                <CardTitle className="text-sm">Clashed schedules</CardTitle>
                <CardDescription className="text-[11px]">Schedules that overlap another schedule for the same worker in the selected period.</CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[11px] text-amber-800">
                  {overview?.summary?.clashed_schedules_count ?? 0} schedule{(overview?.summary?.clashed_schedules_count ?? 0) === 1 ? '' : 's'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 p-2.5">
              {isDashboardBusy ? (
                <DashboardListSkeleton count={4} height="h-14" />
              ) : (overview?.clashed_schedules?.length ?? 0) > 0 ? (
                <div className="space-y-1.5">
                  {(overview?.clashed_schedules ?? []).map(renderClashedScheduleItem)}
                </div>
              ) : (
                <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                  <AlertTriangle className="h-8 w-8 opacity-60" />
                  <div>
                    <p className="text-sm font-medium text-foreground">No clashed schedules</p>
                    <p className="mt-0.5 text-xs">Worker schedule overlaps will appear here.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          </>
          ) : null}
        </div>
        ) : null}
      </section>
      ) : null}

      {visibleFinanceCards.length > 0 || canAccessExpenses || canAccessInvoices || canAccessWorkerPayments ? (
      <section className="space-y-1.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <p className="text-xs font-semibold text-foreground">Cash flow watch</p>
          <p className="text-[11px] text-muted-foreground">Supplier costs, worker payouts, and customer collections</p>
        </div>

        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
          {visibleFinanceCards.map((card) => {
            const cardHref = card.href && canAccessDashboardHref(card.href)
              ? withServiceInstanceId(card.href, serviceInstanceId)
              : null
            const cardClassName = `rounded-xl border px-2 py-1.5 shadow-sm transition-all duration-200 ${card.tone} ${cardHref ? 'hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60' : ''}`
            const content = (
              <>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium">{card.title}</p>
                </div>
                <card.icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              </div>
              <div className="mt-1.5 border-t border-current/15 pt-1.5">
                <p className="text-[10px] uppercase tracking-[0.14em] opacity-70">{card.totalLabel}</p>
                {isDashboardBusy ? <Skeleton className="mt-1 h-5 w-20 bg-current/20" /> : <p className="mt-0.5 text-base font-bold">{card.totalValue}</p>}
              </div>
              </>
            )

            return cardHref ? (
              <Link key={card.title} to={cardHref} className={cardClassName}>
                {content}
              </Link>
            ) : (
              <div key={card.title} className={cardClassName}>
                {content}
              </div>
            )
          })}
        </div>

        <div className="grid gap-2 lg:grid-cols-2 xl:grid-cols-3">
          {canAccessExpenses ? (
          <Card id="dashboard-expenses-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
              <div>
                <CardTitle className="text-sm">Upcoming expenses</CardTitle>
                <CardDescription className="text-[11px]">Purchases and unpaid costs likely to hit cash flow soon.</CardDescription>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Next 7 days</p>
                {isDashboardBusy ? <Skeleton className="ml-auto mt-1 h-5 w-20" /> : <p className="text-base font-semibold text-foreground">{formatDashboardAmount(overview?.financials?.upcoming_expenses_total ?? 0)}</p>}
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 p-2.5">
              {isDashboardBusy ? (
                <DashboardListSkeleton count={4} height="h-14" />
              ) : (overview?.upcoming_expenses?.length ?? 0) > 0 ? (
                <div className="space-y-1.5">
                  {(overview?.upcoming_expenses ?? []).map(renderExpenseItem)}
                </div>
              ) : (
                <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                  <ReceiptText className="h-8 w-8 opacity-60" />
                  <div>
                    <p className="text-sm font-medium text-foreground">No near-term costs</p>
                    <p className="mt-0.5 text-xs">Upcoming purchases and unpaid expenses will appear here.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          ) : null}

          {canAccessInvoices ? (
          <Card id="dashboard-pending-invoices-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
              <div>
                <CardTitle className="text-sm">Pending invoices</CardTitle>
                <CardDescription className="text-[11px]">Customer invoices still waiting to be sent or collected.</CardDescription>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Open balance</p>
                {isDashboardBusy ? <Skeleton className="ml-auto mt-1 h-5 w-20" /> : <p className="text-base font-semibold text-foreground">{formatDashboardAmount(overview?.financials?.pending_invoices_total ?? 0)}</p>}
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 p-2.5">
              {isDashboardBusy ? (
                <DashboardListSkeleton count={4} height="h-14" />
              ) : (overview?.pending_invoices?.length ?? 0) > 0 ? (
                <div className="space-y-1.5">
                  {(overview?.pending_invoices ?? []).map(renderPendingInvoiceItem)}
                </div>
              ) : (
                <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                  <FileClock className="h-8 w-8 opacity-60" />
                  <div>
                    <p className="text-sm font-medium text-foreground">No pending invoices</p>
                    <p className="mt-0.5 text-xs">Draft and issued invoices with balance due will appear here.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          ) : null}

          {canAccessWorkerPayments ? (
          <Card id="dashboard-worker-payments-panel" className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 scroll-mt-24">
            <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
              <div>
                <CardTitle className="text-sm">Worker payout exposure</CardTitle>
                <CardDescription className="text-[11px]">Pending payout periods the company still owes workers.</CardDescription>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Selected window</p>
                {isDashboardBusy ? <Skeleton className="ml-auto mt-1 h-5 w-20" /> : <p className="text-base font-semibold text-foreground">{formatDashboardAmount(overview?.financials?.pending_worker_payments_total ?? 0)}</p>}
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 p-2.5">
              {isDashboardBusy ? (
                <DashboardListSkeleton count={4} height="h-14" />
              ) : (overview?.upcoming_worker_payments?.length ?? 0) > 0 ? (
                <div className="space-y-1.5">
                  {(overview?.upcoming_worker_payments ?? []).map(renderWorkerPaymentItem)}
                </div>
              ) : (
                <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                  <Wallet className="h-8 w-8 opacity-60" />
                  <div>
                    <p className="text-sm font-medium text-foreground">No pending payouts</p>
                    <p className="mt-0.5 text-xs">Worker payout periods with pending amounts will surface here.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          ) : null}
        </div>
      </section>
      ) : null}

      {canAccessJobs || canAccessWorkers || actionShortcuts.length > 0 ? (
      <section ref={recentSection.ref} className="grid gap-2 lg:grid-cols-2 xl:grid-cols-3">
        {canAccessJobs ? (
        <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
          <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
            <div>
              <CardTitle className="text-sm">Recent jobs</CardTitle>
              <CardDescription className="text-[11px]">Latest job records and delivery status.</CardDescription>
            </div>
            {canAccessJobs ? (
              <Button asChild variant="outline" size="sm" className="h-7 rounded-md px-2 text-xs">
                <Link to={withServiceInstanceId('/job', serviceInstanceId)}>
                  View all
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="p-2.5">
            {!shouldLoadRecentSection || jobsLoading ? (
              <div className="space-y-1.5">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="rounded-lg border border-border/70 p-2">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="mt-1.5 h-3 w-20" />
                  </div>
                ))}
              </div>
            ) : jobRows.length > 0 ? (
              <div className="space-y-1.5">
                {jobRows.slice(0, 5).map((job) => (
                  <div key={job.id} className="flex items-center justify-between gap-2 rounded-lg border border-border/70 px-2.5 py-1.5 transition-colors hover:border-violet-200 hover:bg-violet-50/40">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1">
                        {canAccessJobs ? (
                          <JobTitleLink
                            jobId={job.id}
                            title={job.title}
                            jobRef={job.job_ref}
                            serviceInstanceId={serviceInstanceId}
                            className="truncate text-sm font-medium text-foreground hover:text-primary"
                          />
                        ) : (
                          <p className="truncate text-sm font-medium text-foreground">
                            {formatJobTitleWithId({ jobId: job.id, title: job.title, jobRef: job.job_ref })}
                          </p>
                        )}
                        {job.job_ref ? (
                          <Badge variant="outline" className="shrink-0 px-1 py-0 text-[9px]">{job.job_ref}</Badge>
                        ) : null}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">{job.location || 'No location assigned'}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {Number(job.workday_entry_clash_count ?? 0) > 0 ? (
                        <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-800">
                          <AlertTriangle className="h-3 w-3" />
                          {Number(job.workday_entry_clash_count ?? 0)}
                        </Badge>
                      ) : null}
                      {getJobStatusBadge(job.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                <Briefcase className="h-8 w-8 opacity-60" />
                <div>
                  <p className="text-sm font-medium text-foreground">No jobs found</p>
                  <p className="mt-0.5 text-xs">Create your first job to start operational tracking.</p>
                </div>
                {canCreateJobs ? (
                  <Button asChild variant="outline" size="sm" className="h-7 rounded-md">
                    <Link to={withServiceInstanceId('/job/create', serviceInstanceId)}>Create job</Link>
                  </Button>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
        ) : null}

        {canAccessWorkers ? (
        <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
          <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border/70 p-3 pb-2">
            <div>
              <CardTitle className="text-sm">Recent workers</CardTitle>
              <CardDescription className="text-[11px]">Current workforce records and approval state.</CardDescription>
            </div>
            {canAccessWorkers ? (
              <Button asChild variant="outline" size="sm" className="h-7 rounded-md px-2 text-xs">
                <Link to={withServiceInstanceId('/workers', serviceInstanceId)}>
                  View all
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="p-2.5">
            {!shouldLoadRecentSection || workersLoading ? (
              <div className="space-y-1.5">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="rounded-lg border border-border/70 p-2">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="mt-1.5 h-3 w-24" />
                  </div>
                ))}
              </div>
            ) : workerRows.length > 0 ? (
              <div className="space-y-1.5">
                {workerRows.slice(0, 5).map((worker) => (
                  <div key={worker.id} className="flex items-center justify-between gap-2 rounded-lg border border-border/70 px-2.5 py-1.5 transition-colors hover:border-sky-200 hover:bg-sky-50/40">
                    <div className="min-w-0">
                      {canAccessWorkers ? (
                        <Link to={withServiceInstanceId(`/workers/${worker.id}`, serviceInstanceId)} className="truncate text-sm font-medium text-foreground hover:text-primary">
                          {getWorkerName(worker)}
                        </Link>
                      ) : (
                        <p className="truncate text-sm font-medium text-foreground">{getWorkerName(worker)}</p>
                      )}
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">{worker.email || 'No email available'}</p>
                    </div>
                    {getWorkerStatusBadge(worker.status)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex min-h-20 flex-col items-center justify-center gap-1 text-center text-muted-foreground">
                <Users className="h-8 w-8 opacity-60" />
                <div>
                  <p className="text-sm font-medium text-foreground">No workers found</p>
                  <p className="mt-0.5 text-xs">Worker records will appear here once the directory is populated.</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        ) : null}

        {actionShortcuts.length > 0 ? (
        <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
          <CardHeader className="border-b border-border/70 p-3 pb-2">
            <CardTitle className="text-sm">Action shortcuts</CardTitle>
            <CardDescription className="text-[11px]">Move from signal to action without leaving the control view.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5 p-2.5">
            {actionShortcuts.map((item) => (
              <Button key={item.href} asChild variant="outline" size="sm" className="h-8 w-full justify-between rounded-md px-2.5 text-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 hover:shadow-sm">
                <Link to={withServiceInstanceId(item.href, serviceInstanceId)}>
                  <span className="flex items-center gap-1.5"><item.icon className="h-3.5 w-3.5" />{item.title}</span>
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </Button>
            ))}
            {actionShortcuts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/70 px-2.5 py-3 text-xs text-muted-foreground">
                No dashboard shortcuts are available for this role.
              </div>
            ) : null}
            <div className="rounded-lg border border-border/70 bg-muted/25 px-2.5 py-1.5 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                <p>Use this page for planning pressure and cash signals, then jump into finance and scheduling for execution detail.</p>
              </div>
            </div>
          </CardContent>
        </Card>
        ) : null}
      </section>
      ) : null}
    </div>
  )
}
